export const en = {
  app: {
    name: 'Periscope',
    tagline: 'Company management in one place',
  },

  common: {
    loading: 'Loading...',
    error: 'An error occurred',
    newVersionAvailable: 'A new app version is available (v{version})',
    refreshNow: 'Refresh',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    filter: 'Filter',
    apply: 'Apply',
    clear: 'Clear',
    actions: 'Actions',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    none: 'None',
    export: 'Export',
    exportTo: 'Export to',
    downloadFailed: 'Failed to download the file. Please try again.',
    undo: 'Undo',
  },

  // CRM — Spec: docs/superpowers/specs/2026-09-09-crm-module-design.md
  crm: {
    title: 'CRM',
    tabs: { leads: 'Leads', accounts: 'Accounts', deals: 'Deals' },
    accountStatus: {
      lead: 'Lead',
      prospect: 'Prospect',
      customer: 'Customer',
      inactive: 'Inactive',
    },
    leadStatus: {
      new: 'New',
      assigned: 'Assigned',
      converted: 'Converted',
      spam: 'Spam',
      rejected: 'Rejected',
    },
    leadRejectReason: {
      spam: 'Spam',
      not_interested: 'Not interested',
      no_contact: 'No contact',
      out_of_scope: 'Out of scope',
      duplicate: 'Duplicate',
      other: 'Other',
    },
    activityType: {
      note: 'Note',
      call: 'Call',
      meeting: 'Meeting',
      email_out: 'E-mail sent',
      email_in: 'E-mail received',
      stage_change: 'Stage change',
      lead_received: 'Lead received',
      lead_converted: 'Lead converted',
      deal_created: 'Deal created',
      task_done: 'Task completed',
      file: 'File',
    },
    leads: {
      title: 'Lead queue',
      searchPlaceholder: 'Search: e-mail, phone, company, message…',
      allStatuses: 'All statuses',
      newLead: 'New lead',
      empty: 'No leads match the filters',
      selected: 'Selected: {count}',
      selectAllResults: 'Select all results ({count})',
      clearSelection: 'Clear selection',
      assignOwner: 'Assign owner',
      convert: 'Convert',
      hide: 'Hide',
      hidden: 'Lead hidden',
      hiddenMany: 'Hidden: {count}',
      deleted: 'Lead deleted',
      deletedMany: 'Deleted: {count}',
      activeStatuses: 'Active',
      skipped: 'Skipped: {count}',
      reasonLabel: 'Reason',
      restore: 'Restore',
      hideDialog: {
        title: 'Hide lead',
        titleMany: 'Hide {count} leads',
        description: 'The lead disappears from the active view; it can be restored.',
        reason: 'Reason',
        note: 'Note (optional)',
        notePlaceholder: 'e.g. the client picked a competitor',
        submit: 'Hide',
      },
      deleteDialog: {
        title: 'Delete lead?',
        titleMany: 'Delete {count} leads?',
        description: 'This cannot be undone. Converted leads cannot be deleted.',
      },
      columns: {
        received: 'Received',
        source: 'Source',
        contact: 'Contact',
        company: 'Company',
        message: 'Message',
        status: 'Status',
        account: 'Account',
      },
      detail: {
        title: 'Lead details',
        rawPayload: 'Raw payload',
        utm: 'UTM',
        sourceUrl: 'Form URL',
        externalId: 'External id',
        consent: 'Marketing consent',
        timeline: 'History',
      },
      convertDialog: {
        title: 'Convert lead',
        existingAccount: 'Existing account',
        newAccount: 'New account',
        accountName: 'Account name',
        createContact: "Create a contact from the lead's data",
        createDeal: 'Create a deal',
        dealName: 'Deal name',
        dealValue: 'Net value',
        submit: 'Convert',
      },
      created: 'Lead added',
      converted: 'Lead converted',
      restored: 'Lead restored',
      restoredMany: 'Restored: {count}',
      assigned: 'Updated {count} leads',
    },
    accounts: {
      title: 'Accounts',
      searchPlaceholder: 'Search: name, VAT id, city, e-mail…',
      allStatuses: 'All statuses',
      newAccount: 'New account',
      empty: 'No accounts match the filters',
      columns: {
        name: 'Name',
        nip: 'VAT id',
        status: 'Status',
        city: 'City',
        owner: 'Owner',
        openDeals: 'Open deals',
        openValue: 'Open value',
        lastActivity: 'Last activity',
      },
      form: {
        name: 'Name',
        legalName: 'Legal name',
        nip: 'VAT id',
        vatId: 'EU VAT',
        fetchRegistry: 'Fetch data',
        website: 'Website',
        emailDomains: 'E-mail domains',
        emailDomainsHint: 'Comma-separated. Public domains (gmail.com etc.) are ignored.',
        email: 'E-mail',
        phone: 'Phone',
        street: 'Street',
        postalCode: 'Postal code',
        city: 'City',
        country: 'Country',
        industry: 'Industry',
        status: 'Status',
        owner: 'Owner',
        notes: 'Notes',
      },
      detail: {
        deals: 'Deals',
        timeline: 'History',
        tasks: 'Tasks',
        addNote: 'Add note',
        noteePlaceholder: 'What happened?',
        noDeals: 'No deals',
        noActivity: 'No history',
      },
      tabs: {
        overview: 'Overview',
        documents: 'Documents',
      },
      documents: {
        noContractorHint: 'Account documents live on the contractor record in the accounting register. Create the contractor to start adding documents.',
        createAndUpload: 'Create contractor and add a document',
      },
      saved: 'Account saved',
      nipConflict: 'An account with this VAT id already exists',
      contractor: {
        title: 'Contractor',
        none: 'This account is not linked to a contractor in the accounting records yet.',
        add: 'Add to contractors',
        unlink: 'Unlink',
        unlinkConfirmTitle: 'Unlink this contractor?',
        unlinkConfirmBody: 'The CRM account will no longer be linked to this contractor. The contractor and its invoices stay unchanged.',
        open: 'Open contractor record',
        created: 'Contractor created',
        linkedExisting: 'Linked to an existing contractor',
        linked: 'Contractor linked',
        unlinked: 'Contractor unlinked',
        alreadyLinked: 'This account already has a linked contractor',
        match: {
          open: 'Match',
          title: 'Match a contractor',
          empty: 'No similar contractors found. You can add a new one.',
          exact: 'Same VAT id',
          name: 'Similar name',
          link: 'Link',
        },
        loadFailed: 'Failed to load candidates',
        linkedTo: 'Already linked to account "{name}"',
      },
    },
    contacts: {
      title: 'Contacts',
      add: 'Add contact',
      edit: 'Edit contact',
      empty: 'No contacts',
      primary: 'Primary',
      saved: 'Contact saved',
      form: {
        firstName: 'First name',
        lastName: 'Last name',
        jobTitle: 'Job title',
        email: 'E-mail',
        phone: 'Phone',
        isPrimary: 'Primary contact',
        notes: 'Notes',
        submit: 'Save',
      },
    },
    deals: {
      empty: 'No deals',
      lostReason: 'Lost reason',
      lostReasonRequired: 'A loss requires a reason',
      // Board (drag & drop) + deal sheet — spec 2026-09-10
      board: {
        searchPlaceholder: 'Search: name, account…',
        summary: 'Open: {count}',
        weighted: 'weighted {sum}',
        otherCurrency: '(+{count} in {currency})',
        mine: 'Mine',
        overdue: 'Overdue',
        showClosed: 'Closed',
        last30Days: 'last {days} days',
        lostSum: 'lost {sum}',
        staleCount: '{count} without activity',
        overdueCount: '{count} overdue',
        inactiveStage: 'Inactive stage',
        dropHere: 'Drop here',
        dropWon: 'Drop to mark as won',
        dropLost: 'Drop to mark as lost',
        movedTo: 'Moved to: {stage}',
        undoNote: 'Stage change undone',
        moveFailed: 'Could not change stage — previous restored',
        won: 'Won! {name}',
        lost: 'Marked as lost',
        resumed: 'Deal back in the pipeline',
        emptyTitle: 'No deals in the pipeline',
        emptyHint: 'Deals are created by converting a lead.',
        goToLeads: 'Go to leads',
        noMatch: 'Nothing matches the filters',
        refresh: 'Refresh',
        retry: 'Try again',
        shortcuts: {
          title: 'Keyboard shortcuts',
          open: 'Open',
          lift: 'Lift card',
          move: 'Change stage (arrows)',
          drop: 'Drop / cancel',
          nextStage: 'Next stage',
          prevStage: 'Previous stage',
          won: 'Won',
          lost: 'Lost',
          search: 'Search',
        },
      },
      card: {
        noOwner: 'no owner',
        inStage: '{days} days in stage',
        inStageOne: '1 day in stage',
        noContact: 'no contact for {days} days',
        tooltip: 'Last activity: {activity} · in stage since {since}',
        noActivityYet: 'none yet',
      },
      relative: {
        today: 'today',
        tomorrow: 'tomorrow',
        yesterday: 'yesterday',
        inDays: 'in {days} days',
        daysAgo: '{days} days ago',
        overdueDays: '{days} days overdue',
        overdueOne: '1 day overdue',
      },
      dnd: {
        start:
          'Picked up deal "{name}" from stage {stage}. Left and right arrows change the stage, space drops, Escape cancels.',
        over: '"{name}" over stage {stage}',
        end: 'Moved "{name}" to stage {stage}',
        cancel: 'Move cancelled',
        instructions:
          'Press space to pick up a deal. Left and right arrows change the stage, space drops, Escape cancels.',
      },
      close: {
        lostTitle: 'Mark as lost',
        lostConfirm: 'Mark as lost',
        reasons: {
          price: 'Price',
          competition: 'Competition',
          noBudget: 'No budget',
          noDecision: 'No decision',
          noContact: 'No contact',
          other: 'Other',
        },
        wonTitle: 'Mark as won',
        wonBody: '{account} will get the "Customer" status. Value: {value}.',
        wonNote: 'Note (optional)',
        wonConfirm: 'Confirm win',
        wonAccountStays: 'Customer status is kept',
      },
      detail: {
        overline: 'Deal',
        notFound: 'Deal not found or no access',
        openAccount: 'Open account',
        copyLink: 'Copy link',
        linkCopied: 'Link copied',
        createdAgo: 'created {date} ({ago})',
        fromLead: 'from lead',
        unsavedNote: 'Unsaved entry — press Esc again to close',
        nameRequired: 'Name cannot be empty',
        tabs: { history: 'History', data: 'Details' },
      },
      health: {
        fresh: 'Active · {ago}',
        freshNoActivity: 'New · no activity yet',
        stale: 'No activity · {days} days',
        staleOne: 'No activity · 1 day',
        overdue: 'Overdue · {days} days',
        overdueOne: 'Overdue · 1 day',
      },
      stepper: {
        moveTo: 'Move to: {stage} ({p} %)',
        inStageSince: 'In {stage} since {date} ({days} days) · probability {p} %',
        inStageSinceOne: 'In {stage} since {date} (1 day) · probability {p} %',
        won: 'Won',
        lost: 'Lost',
        wonBanner: 'Won {date} · {value}',
        wonCustomer: 'Account has the "Customer" status',
        lostBanner: 'Lost {date}',
        reason: 'Reason: {reason}',
        enteredBy: 'by {author}',
        resume: 'Return to pipeline',
        resumeConfirm: 'The deal returns to {stage}. The lost reason will be cleared.',
      },
      facts: {
        value: 'Net value',
        probability: 'Probability',
        weighted: 'Weighted value',
        expectedClose: 'Expected close',
        owner: 'Owner',
        account: 'Account',
        primaryContact: 'Primary contact',
        project: 'Project',
        notes: 'Notes',
        createdAt: 'Created',
        updatedAt: 'Last change',
        stageDefault: 'stage default: {p} %',
        stageOverrides: 'Changing the stage restores the stage default',
        pastDue: '(overdue)',
        assignMe: 'Assign to me',
        noOwner: '— no owner —',
        noContact: '— none —',
        searchUser: 'Search by name…',
        quick: { week: '+1 week', month: '+1 month', quarter: 'end of quarter', clear: 'Clear' },
        notesPlaceholder: 'Context, agreements, requirements…',
        savedFlash: 'Saved',
      },
      composer: {
        save: 'Save',
        subject: 'Subject',
        time: 'Time',
        today: 'today {time}',
        yesterday: 'yesterday',
        minusHour: '-1 h',
        call: 'Call',
        placeholder: {
          note: 'What happened?',
          call: 'Who and what was agreed?',
          meeting: 'Attendees, decisions, next steps',
          email_out: 'Subject and summary',
          email_in: 'Subject and summary',
        },
      },
      timeline: {
        all: 'All',
        entries: 'Entries',
        stageChanges: 'Stage changes',
        today: 'Today',
        yesterday: 'Yesterday',
        more: 'more',
        empty: 'No entries — log the first call.',
        created: 'Created {date}',
        fullAccountHistory: 'Full account history →',
        reason: 'Reason:',
      },
      nextStep: {
        title: 'What next?',
        overdue: 'Close date passed {days} days ago',
        overdueYesterday: 'Close date passed yesterday',
        noContact: 'No contact for {days} days — call {contact}',
        noContactGeneric: 'No contact for {days} days',
        noValue: 'Add a value — the forecast ignores this deal without it',
        noDate: 'Set the expected close date',
        onTrack: 'On track — next stage: {stage}',
        actions: {
          moveDate: 'Move the date',
          logCall: 'Log a call',
          enterValue: 'Enter a value',
          setDate: 'Set a date',
          moveNext: 'Move →',
          call: 'Call',
        },
      },
    },
  },

  navigation: {
    tags: 'Tags',
    companies: 'Companies',
    unassigned: 'Unassigned',
    invoices: 'Cost invoices',
    sales: 'Sales',
    crm: 'CRM',
    fiscal: 'Fiscalization',
    projects: 'Projects',
    contractors: 'Contractors',
    magazyn: 'Warehouse',
    dashboard: 'Dashboard',
    mileage: 'Mileage',
    employees: 'Employees',
    hr: 'HR',
    documents: 'Documents',
    administration: 'Administration',
    users: 'Users',
    apiApps: 'API applications',
  },

  invoices: {
    title: 'Cost invoices',
    titleWithCompany: 'Cost invoices: {companyName}',
    subtitle: 'View and manage invoices for this company',
    searchPlaceholder: 'Search by invoice number, KSeF number, contractor name, or amount',
    noInvoices: 'No invoices found',
    noInvoicesDescription: 'There are no invoices matching your filters',

    // Table columns
    columns: {
      index: '#',
      pdf: 'PDF',
      source: 'Source',
      contractor: 'Contractor',
      invoiceNumber: 'Invoice Number',
      issueDate: 'Issue Date',
      receivedAt: 'Received',
      status: 'Status',
      exportedAt: 'Exported',
      tags: 'Tags',
      exportStatus: 'Export',
      amount: 'Amount',
      actions: 'Actions',
      detectedNip: 'Detected NIP',
      notes: 'Notes',
      project: 'Project',
    },

    // Source labels
    source: {
      email: 'Email',
      upload: 'Upload',
      ksef: 'KSeF',
      import: 'Import (archive)',
    },

    // Filters
    filters: {
      mpp: {
        all: 'All',
        mppOnly: 'MPP only',
        withoutMpp: 'Without MPP',
      },
      dateType: {
        label: 'Date type',
        issueDate: 'Issue date',
        saleDate: 'Sale date',
        paymentDate: 'Payment date',
        createdAt: 'Created at',
      },
      dateFrom: 'From date',
      dateTo: 'To date',
      status: {
        all: 'All',
        paid: 'Paid',
        unpaid: 'Unpaid',
        exported: 'Exported',
      },
      reimbursement: {
        label: 'Reimbursements',
        all: 'All',
        with: 'With reimbursement',
        pending_approval: 'Pending approval',
        approved: 'For bank export',
        exported: 'Transfer ordered',
        without: 'Without reimbursement',
      },
      applyFilters: 'Apply filters',
      clearAll: 'Clear Filters',
      refresh: 'Refresh',
      noNotes: 'Without note',
      projectAll: 'All',
    },

    // Cost invoice detail: document panel tabs (PDF is the default)
    detail: {
      loadError: 'Failed to load invoice',
      saveSuccess: 'Invoice saved successfully',
      saveError: 'Failed to save invoice',
      vatRecheckSuccess: 'VAT status updated',
      vatRecheckError: 'Failed to recheck VAT status',
      downloadPdfError: 'Failed to download PDF',
      downloadOriginalError: 'Failed to download original',
      invoiceNumberPlaceholder: 'Enter invoice number',
      sellerNamePlaceholder: 'Seller name',
      taxIdPlaceholder: 'Tax ID',
      cityPlaceholder: 'City',
      discardConfirmTitle: 'Discard unsaved changes?',
      discardConfirmMessage: 'You have unsaved changes to this invoice. If you leave now, they will be lost.',
      discardConfirmAction: 'Discard changes',
      tabPdf: 'PDF',
      tabLineItems: 'Line items',
    },

    // Status labels
    status: {
      paid: 'Paid',
      unpaid: 'Unpaid',
      exported: 'Exported',
    },

    // Actions
    actions: {
      open: 'Open',
      viewPdf: 'View PDF',
      editTags: 'Edit tags',
      changeStatus: 'Change status',
      moreActions: 'More actions',
      delete: 'Delete',
    },

    // Bulk actions
    bulk: {
      selected: '{count} selected',
      addTags: 'Add tags',
      removeTags: 'Remove tags',
      markAsPaid: 'Mark as paid',
      markAsUnpaid: 'Mark as unpaid',
      markAsExported: 'Mark as exported',
      addTagsModalTitle: 'Add Tags to Invoices',
      addTagsDescription: 'Select tags to add to {count} invoice(s):',
      statusSuccess: '{count} invoice(s) marked as {status}',
      statusError: 'Failed to update invoice status',
      tagsSuccess: 'Tags added to {count} invoice(s)',
      tagsError: 'Failed to add tags',
      assignToCompany: 'Assign to company',
      assignModalTitle: 'Assign Invoices to Company',
      assignModalDescription: 'Select a company to assign {count} invoice(s) to:',
      assignSuccess: '{count} invoice(s) assigned successfully',
      assignError: 'Failed to assign invoices',
      selectCompany: 'Select company',
      assignToProject: 'Assign to project',
      projectModalTitle: 'Assign Invoices to Project',
      projectModalDescription: 'Select a project to assign {count} invoice(s) to:',
      selectProject: 'Select project',
      noProject: 'No project',
      projectSuccess: 'Project updated for {count} invoice(s)',
      projectError: 'Failed to assign project',
      delete: 'Delete',
      deleteConfirmTitle: 'Delete Invoices',
      deleteSuccess: '{count} invoice(s) deleted',
      deleteError: 'Failed to delete invoices',
    },

    // Single-invoice project assignment (row action on both lists)
    assignProject: {
      title: 'Assign Invoice to Project',
      description: 'Select a project for invoice {invoiceNumber}:',
      success: 'Project assignment updated',
      error: 'Failed to assign project',
    },

    // Assign invoice
    assign: {
      sectionTitle: 'This invoice is unassigned',
      button: 'Assign',
      success: 'Invoice assigned successfully',
      error: 'Failed to assign invoice',
      changeCompany: 'Change company',
      changeCompanyTitle: 'Move invoice to another company',
    },

    // Delete invoice
    delete: {
      confirmTitle: 'Delete Invoice',
      confirmMessage: 'Are you sure you want to permanently delete invoice {invoiceNumber}? This action cannot be undone.',
      success: 'Invoice deleted successfully',
      error: 'Failed to delete invoice',
    },

    // Bulk delete confirmation (admin only)
    bulkDeleteConfirm: 'Delete {count} invoices with their files? This cannot be undone.',

    // Export readiness
    export: {
      selectToExport: 'Please select invoices to export',
      pdfZipLoading: 'Generating PDF ZIP...',
      pdfZipSuccess: 'PDF ZIP downloaded successfully',
      pdfZipError: 'Failed to generate PDF ZIP',
      ready: 'Ready for mBank export',
      issues: {
        no_bank_account: 'Missing bank account (IBAN)',
        invalid_iban: 'Invalid IBAN format',
        no_amount: 'Missing or zero amount',
        no_contractor_name: 'Missing contractor name',
        mpp_no_nip: 'MPP: missing contractor NIP',
        mpp_no_vat: 'MPP: missing VAT amount',
        non_transfer: 'Non-transfer payment method',
        already_paid: 'Already paid',
        already_exported: 'Already exported',
        invalid_date: 'Invalid payment date',
        swift_needs_bic: 'SWIFT transfer requires a BIC — enter it below',
        missing_creditor_city: 'Missing vendor city',
        missing_creditor_country: 'Missing vendor country (intl.)',
        sepa_not_eur: 'SEPA requires EUR currency',
        mpp_intl_conflict: 'MPP not supported for international',
        non_iban_account: 'Non-IBAN account (SWIFT only)',
        not_vat_payer: 'Contractor not on VAT whitelist (no /WL!)',
        invalid_country_format: 'Contractor country is not an ISO code (e.g. "Canada" instead of "CA")',
        no_debtor_account_for_currency: 'Company has no bank account in the invoice currency',
      },
    },

    // Upload invoice
    upload: {
      button: 'Upload Invoice',
      tooltip: 'Upload a PDF invoice',
      modalTitle: 'Upload Invoice',
      descriptionPlaceholder: 'Optional description...',
      success: 'Invoice uploaded successfully',
      notInvoice: 'Document uploaded but not classified as an invoice',
      error: 'Failed to upload invoice',
    },

    reimbursement: {
      pending_approval: 'Employee reimbursement — awaiting approval',
      approved: 'Employee reimbursement — ready for bank export',
      exported: 'Employee reimbursement — transfer ordered',
    },
    // Combined payment — one bank/card transaction covering several invoices
    combinedPayment: {
      approxNotConfirmable: 'Not an exact match — shown for reference only, cannot be linked.',
      button: 'Match payment',
      badgeTooltip: 'Combined payment — paid in one transaction together with other invoices',
      dialogTitle: 'Match a combined payment',
      dialogDescription:
        'Enter the transaction date and amount from the bank statement — the system finds paid cost invoices that sum exactly to that amount (e.g. an Allegro cart from several sellers paid as one charge — the invoices arrive already paid). Invoice statuses are never changed.',
      transactionDate: 'Transaction date (statement)',
      amount: 'Amount (PLN)',
      searchButton: 'Search for matches',
      searchError: 'Failed to search for matches',
      noMatches:
        'No invoice combinations sum to this amount. Searched {count} paid (unlinked) invoices from {from} – {to}.',
      approxHeader:
        'No exact matches — closest combinations below. Check the difference before booking!',
      comboLabel: 'Combination: {count} invoices',
      difference: 'Difference',
      copy: 'Copy',
      copied: 'Copied to clipboard',
      copyError: 'Failed to copy',
      sumLabel: 'Total',
      noteLabel: 'Note',
      notePlaceholder: 'e.g. Allegro, company card...',
      accept: 'Link as a combined payment',
      acceptSuccess: 'Linked {count} invoices to the {amount} payment',
      acceptError: 'Failed to link invoices',
      truncated: 'More combinations may exist — showing the most plausible ones.',
      cardTitle: 'Combined payment',
      cardTransaction: 'Transaction on {date} for {amount}',
      cardCreatedBy: 'Linked by: {name}',
      unlink: 'Undo link',
      unlinkConfirmTitle: 'Undo this combined payment?',
      unlinkConfirmMessage: 'The link between {count} invoices and this transaction will be removed. Invoice statuses stay unchanged.',
      unlinkSuccess: 'Link undone',
      unlinkError: 'Failed to undo the link',
      // Sales side ("Match incoming payment")
      salesButton: 'Match incoming payment',
      salesDialogTitle: 'Match a customer payment',
      salesDialogDescription:
        'Pick the customer and currency, enter the transfer date and amount — the system finds open sale invoices whose outstanding balances sum to the transfer (differences up to 1.00 in the currency are acceptable). Confirming registers a payment for the full outstanding balance on every invoice.',
      customerLabel: 'Customer',
      customerPlaceholder: 'Select a customer',
      noCustomers: 'No customers with open invoices',
      currencyLabel: 'Currency',
      currencyOption: '{currency} — {count} invoices, {outstanding}',
      transferDate: 'Transfer date',
      amountCurrency: 'Amount ({currency})',
      salesNoMatches:
        'No invoice combinations match this amount. Searched {count} open invoices of this customer.',
      salesNotePlaceholder: 'e.g. SEPA transfer, ref 8842...',
      salesAccept: 'Link and settle payment',
      salesAcceptSuccess: 'Settled {count} invoices with the {amount} payment',
      salesDiffInfo:
        'The {diff} difference will be ignored — all invoices will be settled in full.',
      diffTooBig: 'Difference exceeds 1.00 — shown for reference only, cannot be linked.',
      unlinkConfirmMessageSales:
        'The link will be removed and the payments it registered on {count} invoices will be reverted (invoices return to unsettled).',
    },

    // Correction invoice (KOR*) marker on invoice lists
    correction: {
      badge: 'Correction',
      tooltipKor: 'Correction invoice — amounts are differences vs the original invoice',
      tooltipKorZal: 'Correction of an advance-payment invoice',
      tooltipKorRoz: 'Correction of a settlement invoice',
    },

    // Accounting notes on an invoice (Task A3)
    notes: {
      title: 'Notes for accounting',
      count: '{count} note(s)',
      empty: 'No notes yet',
      emptyHint: 'Add a note for the accounting team',
      placeholder: 'e.g. Please book as office cost...',
      addButton: 'Add note',
      addSuccess: 'Note added',
      addError: 'Failed to add note',
      updateSuccess: 'Note updated',
      updateError: 'Failed to update note',
      deleteSuccess: 'Note deleted',
      deleteError: 'Failed to delete note',
      deleteConfirmTitle: 'Delete note',
      deleteConfirmMessage: 'Permanently delete this note? This cannot be undone.',
      editedBy: 'edited {date} by {name}',
      edited: 'edited',
      saveEdit: 'Save',
      cancelEdit: 'Cancel',
    },

    // Tag editor on invoice detail (Task A3)
    tagsEditor: {
      addTag: 'Add tag',
      noTags: 'No tags available',
      updateError: 'Failed to update tags',
    },
  },

  tags: {
    nameRequired: 'Tag name is required',
    createSuccess: 'Tag created successfully',
    updateSuccess: 'Tag updated successfully',
    deleteSuccess: 'Tag deleted successfully',
    title: 'Tag Management',
    subtitle: 'Create, edit and delete tags for cost invoices',
    createNew: 'Create new tag',
    existingTags: 'Existing tags',
    noTags: 'No tags created yet',
    noTagsDescription: 'Create your first tag to start organizing invoices',

    form: {
      name: 'Tag name',
      namePlaceholder: 'e.g. Urgent, Important...',
      color: 'Color',
    },

    actions: {
      add: 'Add',
      edit: 'Edit tag',
      delete: 'Delete tag',
      confirmDelete: 'Are you sure you want to delete this tag?',
      confirmDeleteDescription: 'This action cannot be undone. Invoices with this tag will no longer have it.',
    },

    modules: {
      expense: 'Cost invoices',
      sales: 'Sales',
      contractors: 'Contractors',
    },

    // Default tag names (for reference/display)
    defaults: {
      goods: 'Goods',
      cost: 'Cost',
      fuel: 'Fuel',
      geko: 'GEKO',
      gestGoods: 'GEST Goods',
      reInvoice: 'Re-invoice',
      serviceImport: 'Service import',
      b2b: 'B2B',
    },
  },

  contractors: {
    title: 'Contractors',
    titleWithCompany: 'Contractors: {companyName}',
    subtitle: 'View and manage contractors for this company',
    searchPlaceholder: 'Search by contractor name, NIP, ERP ID, or address',

    role: {
      filterLabel: 'Contractor role',
      all: 'All',
      suppliers: 'Suppliers',
      customers: 'Customers',
      supplier: 'Supplier',
      customer: 'Customer',
      both: 'Supplier & customer',
      none: 'No invoices',
    },
    noContractors: 'No contractors found',
    noContractorsDescription: 'There are no contractors matching your search',
    turnoverHidden: 'Turnover is visible only with invoice access',

    // Table columns
    columns: {
      index: '#',
      name: 'Name',
      nip: 'NIP',
      address: 'Address',
      bankAccounts: 'Bank Accounts',
      tags: 'Tags',
      role: 'Role',
      invoiceCount: 'Purchase inv.',
      yearlyTurnoverNet: 'Purchases (year) net',
      monthlyTurnoverNet: 'Purchases (month) net',
      salesInvoiceCount: 'Sales inv.',
      salesYearlyTurnoverNet: 'Sales (year) net',
      lastInvoiceDate: 'Last Invoice',
      actions: 'Actions',
    },

    // Actions
    actions: {
      edit: 'Edit',
      editTags: 'Edit tags',
    },

    // Edit modal
    editModal: {
      title: 'Edit Contractor',
      name: 'Name',
      nip: 'NIP',
      erpId: 'Accounting program (ERP) ID',
      erpIdPlaceholder: 'e.g. K-000123',
      address: 'Address',
      bankAccounts: 'Bank Accounts',
      tags: 'Tags',
      addBankAccount: 'Add bank account',
    },

    defaultInvoiceTags: 'Default invoice tags',
    defaultInvoiceTagsHint: "Applied automatically to this contractor's invoices on import",

    // Document archive (signed agreements/NDAs)
    documents: {
      upload: 'Upload document',
      download: 'Download document',
      type: 'Document type',
      types: {
        nda: 'NDA',
        umowa: 'Agreement',
        aneks: 'Annex',
        pelnomocnictwo: 'Power of attorney',
        oferta: 'Offer',
        inne: 'Other',
        wz: 'WZ',
        wz_zalacznik: 'WZ attachments',
      },
      label: 'Label',
      fileName: 'File name',
      documentDate: 'Document date',
      expiry: 'Expires',
      note: 'Note',
      deleteConfirm: 'Delete this document?',
      expiryWarning: 'Expires in {days} days',
      expired: 'Expired',
      typeRequired: 'Select a document type',
      fileRequired: 'Select a file',
      generateFromTemplate: 'Generate from template',
      generateTemplate: 'Template',
      generateTemplatePlaceholder: 'Select a template',
      generateNoTemplates: 'No published contractor templates',
      generateSubmit: 'Generate',
      generateSuccess: 'Document generated',
      generateExpiryHint: 'Optional — without an expiry date the document is never reminded about',
      edit: 'Edit document details',
      editTitle: 'Document details',
      saved: 'Document details saved',
      returns: {
        confirmed: 'Returned ✓',
        pending: 'Returned — awaiting confirmation',
        downloadReturned: 'Download signed scan',
        confirm: 'Confirm signature',
        confirmTitle: 'Confirm the signed document was received?',
        confirmSuccess: 'Return confirmed',
      },
    },

    // Detail page
    detail: {
      title: 'Contractor: {name}',
      back: 'Back to Contractors',
      tabDashboard: 'Dashboard',
      tabDocuments: 'Documents',
      year: 'Year',
      totalTurnoverNet: 'Total Net Turnover',
      invoiceCount: 'Invoices',
      averageInvoiceNet: 'Average Invoice Net',
      lastInvoiceDate: 'Last Invoice',
      purchasesNet: 'Purchases net (year)',
      salesNet: 'Sales net (year)',
      balanceNet: 'Balance (sales − purchases)',
      invoiceCounts: 'Purchase / sales inv.',
      lastPurchaseDate: 'Last purchase invoice',
      lastSaleDate: 'Last sales invoice',
      purchasesSeries: 'Purchases net',
      salesSeries: 'Sales net',
      purchaseShare: 'Share of costs %',
      salesShare: 'Share of sales %',
      monthlyTurnover: 'Monthly turnover: purchases vs sales',
      monthlyShare: 'Share of Company Total',
      viewInvoices: 'View Invoices',
      noData: 'No data available for this year',
      netAmount: 'Net',
      grossAmount: 'Gross',
    },
  },

  dashboard: {
    title: 'Dashboard',
    titleWithCompany: 'Dashboard: {companyName}',
    year: 'Year',
    totalCosts: 'Total Costs',
    totalVat: 'Total VAT',
    currentMonthGross: 'Gross ({month})',
    currentMonthVat: 'VAT ({month})',
    grossAmount: 'Gross',
    vatAmount: 'VAT',
    invoiceCount: 'Invoices',
    invoicesMonth: 'Invoices ({month})',
    averageInvoice: 'Average Invoice',
    topPaymentMethod: 'Top Payment Method',
    monthlyCosts: 'Monthly Costs',
    monthlyTagCosts: 'Monthly Net Costs by Tag',
    monthlyPaymentMethodCosts: 'Monthly Costs by Payment Method',
    unpaidByMonth: 'Unpaid by Month',
    unpaidTotal: 'Unpaid (Year)',
    unpaidCurrentMonth: 'Unpaid ({month})',
    noData: 'No data available for this year',
    paymentMethods: {
      transfer: 'Transfer',
      card: 'Card',
      cash: 'Cash',
      other: 'Other',
    },
    financial: {
      revenueYtd: 'Revenue (year)',
      costYtd: 'Costs (year)',
      margin: 'Margin (year)',
      marginOfRevenue: '{pct}% of revenue',
      mom: '{pct}% MoM',
      momTitle: 'Month-over-month revenue change',
      vatPosition: 'VAT position',
      vatPayable: 'payable',
      vatRefund: 'refundable',
      vatNeutral: 'balanced',
      receivables: 'Receivables',
      payables: 'Payables',
      overdue: 'Overdue: {amount}',
      allOnTime: 'Nothing overdue',
      revenueVsCosts: 'Revenue vs Costs',
      revenue: 'Revenue',
      costs: 'Costs',
      marginDelta: 'Margin',
      marginPctTitle: 'Margin % (monthly)',
      marginPct: 'Margin %',
      cashFlow: 'Cash Flow',
      cashIn: 'Inflows',
      cashOut: 'Outflows',
      cashNet: 'Net',
      aging: 'Receivables & Payables Aging',
      agingReceivables: 'Receivables',
      agingPayables: 'Payables',
      bucketCurrent: 'Not yet due',
      bucket30: '1–30 days',
      bucket60: '31–60 days',
      bucket90: '61–90 days',
      bucketOlder: '90+ days',
      topCustomers: 'Top 5 Customers',
      topVendors: 'Top 5 Vendors',
      invoicesCount: 'Invoices: {count}',
      noCounterparties: 'No data',
      noSales: 'No sales data yet — connect KSeF to see revenue and margin.',
      costSection: 'Cost Analysis',
      showCostDetails: 'Show cost details',
      hideCostDetails: 'Hide cost details',
    },
    health: {
      section: 'Company health',
      sectionSubtitle: 'Revenue, costs, margin, cash and payment discipline',
    },
    people: {
      section: 'People',
      sectionSubtitle: 'What employees really cost, and where that money goes',
      allEmployees: 'All employees',
      employerCostYtd: 'Employer cost (YTD)',
      employerCostHint: 'Gross + employer contributions',
      netYtd: 'Net salaries (YTD)',
      netHint: 'What actually lands in the pocket',
      taxWedgeYtd: 'Tax wedge (ZUS + PIT, YTD)',
      taxWedgeShare: '{pct}% of employer cost',
      avgCostPerEmployee: 'Cost per employee',
      avgCostHint: 'Average, year to date',
      headcount: 'Headcount',
      headcountHint: 'Active today (B2B excluded)',
      headcountValue: '{count} people',
      costStackTitle: 'Where the money goes — monthly cost of an employee',
      costStackHint:
        'Each bar is the total employer cost for that month, split into what the employee receives and what goes to the state.',
      costBaseTitle: 'People cost vs invoice cost',
      costBaseHint: 'What the company’s cost base is made of, month by month.',
      byTypeTitle: 'Employer cost by contract type',
      byTypeHint: 'The types sum to total employer cost (B2B excluded).',
      net: 'Net',
      pit: 'PIT',
      employeeZus: 'Employee ZUS',
      health: 'Health contribution',
      employerZus: 'Employer ZUS',
      gross: 'Gross',
      employerCostTotal: 'Total employer cost',
      payrollCost: 'Employer cost (payroll)',
      b2bCost: 'B2B (inside invoice costs)',
      otherInvoiceCost: 'Other invoice costs',
      costBaseTotal: 'Total cost base',
      peopleShare: 'People share',
      b2bTooltipNote:
        'B2B is already part of invoice costs — we show it separately, but never add it to employer cost.',
      b2bFootnote:
        'A B2B contractor’s cost reaches the company as a purchase invoice, so it is already inside invoice costs. We carve it out to show how much of the cost base is people — it is never added to employer cost, which would double-count it.',
      incompleteTitle: '{count} employees without a rate or contract type — costs are incomplete',
      incompleteBody:
        'These employees were skipped in the calculation, so the amounts in this section are understated.',
      incompleteCta: 'Complete their data',
      noPayroll: 'No payroll data for this year',
    },
    projects: {
      section: 'Projects',
      sectionSubtitle: 'Project profitability — profit and loss against revenue and cost',
      allProjects: 'All projects',
      plTitle: 'Profit and loss by project',
      plHint: 'Bars to the right are profit, to the left loss. Hover for revenue, cost and margin.',
      profit: 'Profit',
      loss: 'Loss',
      revenue: 'Revenue',
      cost: 'Cost',
      margin: 'Margin',
      colProject: 'Project',
      othersLabel: 'Others ({count})',
      rollupTooltip: '{count} smaller projects, rolled up.',
      rollupNote:
        'Showing the {shown} projects with the largest result (by absolute value). The remaining {count} are rolled up into a single “Others” row.',
      empty: 'No projects yet. Create one to track its profitability.',
    },
    counterparties: {
      section: 'Counterparties',
      sectionSubtitle: 'Largest customers and vendors in the selected year',
    },
  },

  fx: {
    title: 'NBP Rates',
    subtitle: '30-day trend',
    effectiveDate: 'as of {date}',
    change30dLabel: '30-day change',
    loading: 'Loading rates…',
    loadError: 'Failed to load NBP rates',
    empty: 'No rate data available',
    plnEquivalent: '≈ {amount} PLN · NBP rate {rate} as of {date}',
    approxPln: '≈ {amount} PLN',
    noRate: 'no rate available',
  },

  sales: {
    title: 'Sales',
    titleWithCompany: 'Sales: {companyName}',
    subtitle: 'Revenue and sales invoices synced from KSeF',
    totalNet: 'Revenue (year)',
    currentMonthNet: 'Revenue ({month})',
    invoiceCount: 'Invoice count',
    avgNet: 'Average invoice',
    monthlyRevenue: 'Monthly revenue',
    netAmount: 'Net',
    noData: 'No sales invoices yet. Data will appear once KSeF sync runs.',
    searchPlaceholder: 'Search by invoice number or customer',
    noInvoices: 'No invoices found',
    noInvoicesDescription: 'There are no invoices matching your filters',
    columns: {
      customer: 'Customer',
      invoiceNumber: 'Invoice Number',
      saleDate: 'Sale Date',
      net: 'Net',
      vat: 'VAT',
      gross: 'Gross',
      ksefNumber: 'KSeF No.',
      tags: 'Tags',
      settlement: 'Settlement',
      project: 'Project',
      actions: 'Actions',
    },

    filters: {
      clearAll: 'Clear Filters',
    },

    // Derived settlement of a sale invoice (Task A3)
    settlement: {
      filterLabel: 'Settlement',
      all: 'All',
      unpaid: 'Unpaid',
      partial: 'Partial',
      paid: 'Paid',
      overdue: 'Overdue',
      overdueDays: 'Overdue ({days} d.)',
      outstandingLeft: '{amount} left',
    },

    // Payment registration + history (Task A3)
    payments: {
      register: 'Register payment',
      dialogTitle: 'Register payment',
      dialogDescription: 'Invoice {invoiceNumber} — outstanding {outstanding}',
      amountLabel: 'Amount ({currency})',
      dateLabel: 'Payment date',
      noteLabel: 'Note (optional)',
      notePlaceholder: 'e.g. bank transfer reference...',
      submit: 'Save payment',
      success: 'Payment registered',
      overpaymentWarning: 'Payment registered — the invoice is now overpaid',
      error: 'Failed to register payment',
      invalidAmount: 'Amount must be greater than 0',
      historyTitle: 'Payment history',
      noPayments: 'No payments recorded',
      paidLabel: 'Paid',
      outstandingLabel: 'Outstanding',
      deleteConfirmTitle: 'Delete payment',
      deleteConfirmMessage: 'Permanently delete this payment record? This cannot be undone.',
      deleteSuccess: 'Payment deleted',
      deleteError: 'Failed to delete payment',
      recordedBy: 'recorded by {name}',
    },

    // Receivables by customer (Task A3)
    receivables: {
      title: 'Receivables',
      subtitle: 'Customers with open sale invoices',
      customer: 'Customer',
      invoiceCount: 'Invoices',
      outstanding: 'Outstanding',
      overdue: 'Overdue',
      oldestDueDate: 'Oldest due date',
      empty: 'No outstanding receivables',
      rowHint: 'Click to filter the invoice list',
    },

    // Sales report: FVs of a given sales month (missing sale date = issue
    // date), sorted by issue date ascending.
    report: {
      button: 'Report',
      title: 'Sales report — {month}',
      monthLabel: 'Sales month',
      yearLabel: 'Sales year',
      copy: 'Copy to clipboard',
      copied: 'Copied to clipboard',
      copyError: 'Failed to copy to clipboard',
      copyValue: 'Copy value',
      count: 'Invoice count: {count}',
      emptyDescription: 'No sale invoices in the selected month',
      columns: {
        lp: 'No.',
        saleDate: 'Sale date',
        issueDate: 'Issue date',
        invoiceNumber: 'Invoice number',
        ksefNumber: 'KSeF number',
        customer: 'Customer',
        netAmount: 'Net amount',
        currency: 'Currency',
      },
    },

    // Customer statement PDF (statement of account / balance confirmation).
    statement: {
      button: 'Statement PDF',
      title: 'Customer statement',
      description: "Generate a PDF with the customer's balance as at the chosen date.",
      descriptionContractor: 'Generate a PDF with the balance of {name} as at the chosen date.',
      customer: 'Customer',
      customerPlaceholder: 'Select a customer',
      noCustomers: 'No customers with sales invoices',
      type: 'Document type',
      typeStatement: 'Statement of account',
      typeBalanceConfirmation: 'Balance confirmation',
      lang: 'Document language',
      langPl: 'Polish',
      langEn: 'English',
      range: 'Date range',
      rangeYtd: 'Year to date',
      rangeCustom: 'Custom',
      from: 'From',
      to: 'To (balance as at)',
      scope: 'Content',
      scopeOpen: 'Outstanding only',
      scopeAll: 'Include paid',
      asOfHint: 'Payments dated after the end of the range are not included.',
      download: 'Download PDF',
      downloaded: 'Statement downloaded',
      loadFailed: 'Could not load the customer list',
    },
  },

  // Projects (Task B3): per-company cost/revenue attribution with P&L
  projects: {
    title: 'Projects',
    titleWithCompany: 'Projects: {companyName}',
    subtitle: 'Cross purchase/sales profit and loss by project',
    newProject: 'New project',
    noProjects: 'No projects yet',
    noProjectsDescription: 'Create your first project to start attributing invoices',
    loadError: 'Failed to load projects',
    partialBadge: 'partial',
    partialTooltip:
      'Some line items carry their own projects — they count there, while the remaining invoice value goes to the whole-invoice project',
    columns: {
      name: 'Name',
      status: 'Status',
      cost: 'Costs',
      revenue: 'Revenue',
      profit: 'Profit/Loss',
      margin: 'Margin %',
      invoiceCount: 'Invoices',
      actions: 'Actions',
    },
    status: {
      active: 'Active',
      closed: 'Closed',
    },
    form: {
      createTitle: 'New project',
      editTitle: 'Edit project',
      name: 'Name',
      namePlaceholder: 'e.g. Warehouse construction',
      code: 'Code (optional)',
      codePlaceholder: 'e.g. HALA-1',
      description: 'Description (optional)',
      descriptionPlaceholder: 'What is this project about...',
      color: 'Color',
      nameRequired: 'Project name is required',
      submitCreate: 'Create project',
      submitSave: 'Save changes',
    },
    actions: {
      edit: 'Edit',
      close: 'Close project',
      reopen: 'Reopen project',
    },
    createSuccess: 'Project created',
    createError: 'Failed to create project',
    updateSuccess: 'Project updated',
    updateError: 'Failed to update project',
    closeSuccess: 'Project closed',
    reopenSuccess: 'Project reopened',
    statusError: 'Failed to change project status',
    closeConfirmTitle: 'Close project',
    closeConfirmMessage:
      'Close project "{name}"? All assignments and history stay intact — the project just stops appearing in assignment selectors.',
    analytics: {
      kpiCost: 'Costs (net)',
      kpiRevenue: 'Revenue (net)',
      kpiProfit: 'Profit/Loss',
      kpiMargin: 'Margin',
      noRevenue: 'no revenue',
      invoiceCounts: '{purchase} cost / {sale} sales invoices',
      monthlyTitle: 'Costs vs revenue (last 12 months)',
      topCostContractors: 'Top cost contractors',
      topRevenueCustomers: 'Top revenue customers',
      noCounterparties: 'No data',
      invoicesTitle: 'Invoices attributed to this project',
      direction: 'Direction',
      directionPurchase: 'Cost',
      directionSale: 'Sale',
      counterparty: 'Counterparty',
      invoiceNumber: 'Number',
      date: 'Date',
      netPln: 'Net PLN',
      scope: 'Scope',
      scopeWhole: 'whole invoice',
      scopeLines: 'line items',
      noInvoices: 'No invoices attributed to this project yet',
      loadError: 'Failed to load project analytics',
      notFound: 'Project not found',
      backToList: 'Back to projects',
    },
    selector: {
      label: 'Project',
      none: 'None',
      noProject: 'No project',
      closedSuffix: '(closed)',
      inherits: 'Inherits:',
      assignError: 'Failed to update project assignment',
    },
    autoBadge: 'auto',
    autoTooltip:
      'Assigned automatically by a tag rule — removing the matching tag will unassign it',
    correctionAutoTooltip:
      'Project inherited from the corrected invoice — a correction automatically follows the invoice it corrects.',
    tagRules: {
      sectionTitle: 'Tag rules',
      description:
        'Invoices carrying any of the selected tags are assigned to this project automatically. Removing the last matching tag reverts a rule-made assignment; manual assignments are never touched.',
      expenseGroup: 'Expense tags',
      salesGroup: 'Sales tags',
      noTags: 'This company has no tags yet',
      loadError: 'Failed to load tag rules',
      saveError: 'Failed to save tag rules',
      applyConfirmTitle: 'Apply rules to existing invoices',
      applyConfirmMessage:
        'Found {matched} invoice(s) with these tags. Assign {assigned} to the project? ({conflicts} skipped — they already belong to another project)',
      applyConfirmAction: 'Assign',
      applySuccess: 'Assigned {assigned} invoice(s) to the project ({conflicts} skipped)',
      applyError: 'Failed to apply tag rules',
      noMatches: 'No existing invoices carry the selected tags',
    },
  },

  export: {
    mbank: {
      loadFailed: 'Failed to load invoice details',
      merged: 'Payments merged. {regular} regular, {unchanged} unchanged.',
      unmerged: 'Payments unmerged.',
      noAccountForCurrency: 'No company bank account for currency {currency}',
      generated: 'Export file generated successfully',
      generateFailed: 'Failed to generate export',
      executionDatePast: 'Execution date is in the past — pick a new date',
      missingAccountOrName: 'Some payments are missing bank account or contractor name',
      swiftRequiresBic: 'SWIFT payments require SWIFT/BIC code',
      // Why the Export button is disabled — the operator used to get a dead
      // button with no explanation (foreign SWIFT invoices, 2026-07 audit).
      blockers: {
        title: 'Export is blocked',
        warningsTitle: 'Check before exporting',
        nothingToExport: 'Nothing selected for export',
        noDebtorAccount: 'No company bank account in {currency} — add one in company settings',
        noBankAccount: 'Payments without a beneficiary bank account: {count}',
        noContractorName: 'Payments without a contractor name: {count}',
        mppIncomplete: 'MPP payments missing contractor NIP or VAT amount: {count}',
        swiftBic: 'Payments with a missing or invalid SWIFT/BIC code: {count}',
        missingCityCountry: 'International payments missing city or country: {count}',
        invalidDate: 'Payments with an invalid payment date: {count} (or tick "Override all payment dates")',
        invalidCountry: 'Payments whose country is not a 2-letter ISO code: {count} — it will be omitted from the file',
      },
      reimbursements: {
        title: 'Employee reimbursements',
        employee: 'Employee',
        account: 'Account',
        transferTitle: 'Transfer title',
        amount: 'Amount',
      },
    },
    formats: {
      xlsx: 'Excel (XLSX)',
      csv: 'CSV',
      pdf: 'PDF',
    },
  },

  pagination: {
    firstPage: 'First page',
    prevPage: 'Previous page',
    nextPage: 'Next page',
    lastPage: 'Last page',
    page: 'Page',
    of: 'of',
    itemsPerPage: 'Items per page',
    showing: 'Showing {from} to {to} of {total}',
  },

  selectionSummary: {
    title: 'Selected ({count}) —',
    net: 'net',
    gross: 'gross',
    missingRate: '{count} item(s) without FX rate',
  },

  ksef: {
    metadata: {
      verification: 'KSeF verification',
      verifyOnKsef: 'Verify on ksef.mf.gov.pl',
      copy: 'Copy',
      copied: 'Copied to clipboard',
      invoiceData: 'Invoice data',
      form: 'Form',
      rodzaj: 'Type',
      wariant: 'Variant',
      wariantN: 'Variant {n}',
      placeOfIssue: 'Place of issue',
      xmlDate: 'XML date',
      system: 'System',
      period: 'Period',
      correction: 'Correction',
      corrects: 'Corrects invoice',
      correctionReason: 'Correction reason',
      buyer: 'Buyer',
      name: 'Name',
      nip: 'NIP',
      clientNo: 'Customer no.',
      address: 'Address',
      email: 'Email',
      phone: 'Phone',
      thirdParty: 'Third party',
      vatBreakdown: 'VAT rate breakdown',
      rate: 'Rate',
      net: 'Net',
      vat: 'VAT',
      bankAccounts: 'Bank accounts',
      paid: 'Paid',
      otherPayment: 'Other payment: {value}',
      annotations: 'Annotations',
      additionalDescription: 'Additional description',
      order: 'Order',
      orderValue: 'Value: {value}',
      orderExternalNo: 'External no.: {value}',
      orderInternalNo: 'Internal no.: {value}',
      deliveryNotes: 'Delivery notes (WZ)',
      settlement: 'Settlement',
      charges: 'Charges',
      deductions: 'Deductions',
      reason: 'Reason',
      totalCharges: 'Total charges',
      totalDeductions: 'Total deductions',
      amountDue: 'Amount due',
      amountToRefund: 'Overpayment to settle',
      paymentDate: 'Payment date',
      amount: 'Amount',
      sellerContact: 'Seller contact',
      rodzajLabels: {
        VAT: 'VAT invoice',
        KOR: 'Correction',
        ZAL: 'Advance',
        ROZ: 'Settlement',
        UPR: 'Simplified',
        ROD: 'Fiscal receipt',
        WEW: 'Internal',
        VAT_MP: 'Margin',
        VAT_RR: 'Flat-rate farmer',
      },
      wariantLabels: {
        '1': 'Standard',
        '2': 'Correcting',
        '3': 'Advance',
        '4': 'Final',
        '5': 'Simplified',
        '6': 'Pro forma',
      },
      adnotacjeLabels: {
        P_16: 'Cash method',
        P_17: 'Self-billing',
        P_18: 'Margin scheme',
        P_18A: 'Margin – travel agency',
        P_19: 'VAT exemption',
        P_19A: 'Legal basis for exemption',
        P_19B: 'Directive provision',
        P_19C: 'Other legal basis',
        P_20: 'Domestic reverse charge',
        P_21: 'EU reverse charge',
        P_22: 'New means of transport (ICS)',
        P_23: 'Supply outside the EU',
        P_106E_2: 'Triangular procedure',
        P_106E_3: 'Third-party country',
        zwolnienieP19A: 'Legal basis for exemption',
      },
    },
    enabledToast: 'KSeF enabled',
    disabledToast: 'KSeF disabled',
    configUpdateFailed: 'Failed to update KSeF config',
    environmentUpdateFailed: 'Failed to update environment',
    syncDateUpdateFailed: 'Failed to update sync date',
    certificateRemoved: 'Certificate removed',
    certificateRemoveFailed: 'Failed to remove certificate',
    certificateUploadFailed: 'Failed to upload certificate',
    syncCompletedWithErrors: 'Sync completed with errors: {errors}',
    title: 'KSeF Integration',
    enabled: 'Enabled',
    environment: 'Environment',
    environmentTest: 'Test',
    environmentProduction: 'Production',
    syncFromDate: 'Sync from date',
    certificate: 'Certificate',
    certificateUploaded: 'Certificate uploaded',
    certificateNotUploaded: 'No certificate',
    certificateFingerprint: 'Fingerprint',
    certificateExpiry: 'Expires',
    certificatePassword: 'Password (for encrypted key or .p12/.pfx)',
    certificateFile: 'Certificate file (.crt, .pem, .p12, .pfx)',
    privateKeyFile: 'Private key file (.key, .pem)',
    uploadCertificate: 'Upload Certificate',
    removeCertificate: 'Remove Certificate',
    testConnection: 'Test Connection',
    testConnectionSuccess: 'Connection successful',
    testConnectionFailed: 'Connection failed',
    syncNow: 'Sync Now',
    syncSuccess: 'KSeF sync completed: {imported} imported, {skipped} skipped',
    syncError: 'KSeF sync failed',
    lastSync: 'Last sync',
    lastSyncStatus: 'Status',
    lastSyncError: 'Error',
    syncStatusSuccess: 'Success',
    syncStatusError: 'Error',
    syncStatusInProgress: 'In progress',
  },

  fiscal: {
    title: 'Fiscalization',
    comingSoon: 'Coming soon',
    tabs: {
      dashboard: 'Dashboard',
      invoices: 'Invoices',
      receipts: 'Receipts',
      printers: 'Printers',
      settings: 'Settings',
    },
    environment: {
      demo: 'Demo',
      test: 'Test',
      production: 'Production',
    },
    environmentDefaultSuffix: '(default)',
    invoiceStatus: {
      QUEUED: 'Queued',
      PROCESSING: 'Processing',
      RETRY_WAIT: 'Retry pending',
      CONFIRMED: 'Confirmed',
      REJECTED: 'Rejected',
    },
    receiptStatus: {
      QUEUED: 'Queued',
      DISPATCHED: 'Dispatched',
      CONFIRMED: 'Confirmed',
      REJECTED: 'Rejected',
    },
    columns: {
      status: 'Status',
      number: 'Number',
      buyer: 'Buyer',
      amount: 'Amount',
      ksefNumber: 'KSeF number',
      printer: 'Printer',
      mode: 'Mode',
      jpkid: 'JPK ID',
      createdAt: 'Created',
      actions: 'Actions',
      retry: 'Retry',
      details: 'Details',
    },
    accessDenied: {
      description: "You don't have access to the fiscalization module in this company.",
    },
    dashboard: {
      invoicesToday: 'Invoices today',
      invoicesMonth: 'Invoices this month',
      receiptsToday: 'Receipts today',
      receiptsMonth: 'Receipts this month',
      rejectedTotal: 'Rejected total',
      printersOnline: 'Printers online',
      chartInvoicesTitle: 'Invoices — last 30 days',
      chartReceiptsTitle: 'Receipts — last 30 days',
      confirmed: 'Confirmed',
      rejected: 'Rejected',
      notConfiguredTitle: 'Fiscalization module not configured',
      notConfiguredDescription:
        'Enable KSeF and/or fiscal receipts integration in settings to see statistics.',
      goToSettings: 'Go to settings',
    },
    invoices: {
      searchPlaceholder: 'Search by number or buyer…',
      statusAll: 'All statuses',
      limitLabel: 'Limit',
      refresh: 'Refresh',
      empty: {
        title: 'No fiscal invoices',
        description: 'No invoices match the selected filters',
      },
      detail: {
        title: 'Invoice {number}',
        status: 'Status',
        error: 'Error',
        attempts: 'Attempts: {count}',
        nextRetryAt: 'Next retry: {date}',
        buyer: 'Buyer',
        amount: 'Amount',
        ksefNumber: 'KSeF number',
        qrTitle: 'QR code (KSeF verification)',
        verificationLink: 'Open KSeF verification page',
        downloadXml: 'Download XML',
        downloadUpo: 'Download UPO',
        upoUnavailable: 'UPO not yet available',
        eventsTitle: 'Event history',
        noEvents: 'No events',
        retry: 'Retry',
        retryConfirmTitle: 'Retry invoice processing?',
        retryConfirmMessage: 'Invoice {number} will be resubmitted to KSeF.',
        retrySuccess: 'Invoice processing retried',
        retryConflict: "This invoice can't be retried right now (status changed)",
        retryError: 'Failed to retry the invoice',
        downloadError: 'Failed to download the file',
      },
    },
    receipts: {
      printerAll: 'All printers',
      refresh: 'Refresh',
      mode: {
        eparagon: 'E-receipt',
        paper: 'Paper',
      },
      empty: {
        title: 'No fiscal receipts',
        description: 'No receipts match the selected filters',
      },
      detail: {
        title: 'Receipt {jpkid}',
        status: 'Status',
        error: 'Error',
        printer: 'Printer',
        mode: 'Mode',
        amount: 'Amount',
        jpkid: 'JPK ID',
        downloadPdf: 'Download PDF',
        pdfUnavailable: 'PDF not yet available',
        eDocumentTitle: 'E-document',
        eDocumentStatus: 'Status',
        eDocumentCode: 'Code',
        eventsTitle: 'Event history',
        noEvents: 'No events',
        retry: 'Retry',
        retryConfirmTitle: 'Retry receipt processing?',
        retryConfirmMessage: 'The receipt will be resubmitted to the fiscal printer.',
        retrySuccess: 'Receipt processing retried',
        retryConflict: "This receipt can't be retried right now (status changed)",
        retryError: 'Failed to retry the receipt',
        downloadError: 'Failed to download the file',
      },
    },
    printers: {
      refresh: 'Refresh',
      add: 'Add printer',
      addTitle: 'New printer',
      editTitle: 'Edit printer: {code}',
      codeLabel: 'Code',
      codeHint: 'Lowercase letters, digits and hyphens, 1-32 characters (e.g. kasa-1)',
      codeInvalid: 'Code must be lowercase letters, digits and hyphens (1-32 characters)',
      nameLabel: 'Name',
      namePlaceholder: 'e.g. Fiscal printer – front desk',
      nameRequired: 'Name is required',
      timeInvalid: 'Time must be in HH:MM format',
      dailyReportModeLabel: 'Daily report mode',
      dailyReportMode: {
        central: 'Central',
        centralHint: 'The server tells the agent to run the daily report at the scheduled time.',
        agent: 'Local (agent)',
        agentHint: 'The agent decides on its own when to run the daily report, based on its own schedule.',
      },
      dailyReportTimeLabel: 'Daily report time',
      activeLabel: 'Active',
      create: 'Create',
      save: 'Save',
      createSuccess: 'Printer created',
      createFailed: 'Failed to create the printer',
      updateSuccess: 'Printer updated',
      updateFailed: 'Failed to update the printer',
      edit: 'Edit printer',
      regenerateToken: 'New token',
      delete: 'Delete printer',
      deleteConfirmTitle: 'Delete printer {code}?',
      deleteConfirmMessage: "This can't be undone. The agent's access token will stop working.",
      deleteSuccess: 'Printer deleted',
      deleteFailed: 'Failed to delete the printer',
      regenerateConfirmTitle: 'Generate a new token for {code}?',
      regenerateConfirmMessage:
        "The current token stops working immediately — the agent will lose its connection until it's updated with the new token.",
      regenerateSuccess: 'New token generated',
      regenerateFailed: 'Failed to generate the token',
      tokenRevealTitle: 'Agent token — printer {code}',
      tokenRevealDescription: "The fiscal printer agent's new access token.",
      tokenRevealWarning:
        "This token is shown only once. Copy it now and store it somewhere safe — once this dialog closes, it can't be retrieved again.",
      copyToken: 'Copy token',
      tokenCopied: 'Token copied',
      copySnippet: 'Copy command',
      snippetCopied: 'Command copied',
      dockerSnippetTitle: 'Ready-to-run agent command',
      dockerSnippetHint: "Replace <IP-drukarki> with the fiscal printer's IP address on the local network.",
      close: 'Got it, close',
      empty: {
        title: 'No printers configured',
        description: 'Add your first printer to start recording fiscal receipts.',
      },
      deviceStatus: 'Device status',
      deviceStatusUnknown: 'No data from the agent',
      taxRatesTitle: 'VAT rates',
      taxRatesEmpty: 'No rate data',
      protectedMemoryFree: 'Free protected memory',
      queueDepth: 'Job queue',
      lastDailyReport: 'Last daily report',
      lastDailyReportNever: 'Never',
      dailyReportOverdue: 'Daily report overdue',
      blackout: 'Blackout (day closed)',
      agentVersion: 'Agent version',
      lastHeartbeat: 'Last heartbeat',
      lastHeartbeatNever: 'No heartbeat yet',
      online: 'Online',
      offline: 'Offline',
      inactive: 'Inactive',
      loadFailed: 'Failed to load the printer list',
    },
    settings: {
      loadFailed: 'Failed to load fiscalization module settings',
      readOnlyNote: 'Only a company administrator can change these settings.',
      moduleTitle: 'Module enable',
      moduleEnabled: 'Fiscalization module enabled',
      moduleHint:
        'Disabling stops sending new invoices and receipts to KSeF and printers — historical data stays visible.',
      environmentTitle: 'KSeF environment',
      environmentHint: {
        demo: "KSeF's test environment, not linked to the company's real data — for integration trials.",
        test: "KSeF's test environment with production-like data — for acceptance testing before go-live.",
        production: 'The real KSeF environment — issued invoices are legally binding.',
      },
      environmentConfirmTitle: 'Switch environment to: {environment}?',
      environmentConfirmProductionWarning:
        'WARNING: you are switching the module to the PRODUCTION KSeF environment. This change was unlocked by an operator decision. From now on, every issued invoice will be LEGALLY BINDING and will reach the National e-Invoice System in production. Make sure the production certificate is configured correctly.',
      environmentConfirmGenericWarning:
        'Changing the environment affects where issued invoices and receipts are sent. Make sure the right certificate is configured for this environment.',
      environmentUpdateSuccess: 'KSeF environment changed',
      environmentUpdateFailed: 'Failed to change the environment',
      environmentDefaultNote:
        'This is the DEFAULT environment. Each API token (application) can carry its own environment — set when creating or editing the token — and the environment is frozen onto every invoice when it is accepted.',
      environmentSyncNote:
        'Applies only to issuing invoices and receipts in this module. Downloading invoices from KSeF (sync) has its own setting in the company profile and stays independent.',
      certificatesTitle: 'Certificates',
      certificatesHint: 'A qualified or KSeF certificate is needed to sign invoices in a given environment.',
      certificateMissing: 'No certificate',
      certificateFingerprint: 'Fingerprint',
      certificateExpiry: 'Valid until',
      certificateExpired: 'Certificate expired',
      certificateExpiringSoon: 'Expires in {days} days',
      uploadCertificate: 'Upload certificate',
      replaceCertificate: 'Replace certificate',
      uploadCertificateTitle: 'Upload certificate — {environment}',
      certificateFile: 'Certificate file (.p12, .pfx, .pem, .crt)',
      certificatePassword: 'Certificate password (optional for .pem/.crt)',
      certificateFormatLabel: 'Detected format',
      certificateFormatP12: 'PKCS#12 (.p12/.pfx)',
      certificateFormatPem: 'PEM (.pem/.crt)',
      certificateWrongType: 'Invalid certificate file type. Allowed: .p12, .pfx, .pem, .crt',
      certificateTooLarge: 'Certificate file is too large (max 10MB)',
      certificateUploadSuccess: 'Certificate uploaded',
      certificateUploadFailed: 'Failed to upload the certificate',
      deleteCertificate: 'Delete certificate — {environment}',
      deleteCertificateConfirmTitle: 'Delete certificate — {environment}?',
      deleteCertificateConfirmMessage: 'Without a certificate, sending invoices to KSeF in this environment will stop working.',
      certificateDeleteSuccess: 'Certificate deleted',
      certificateDeleteFailed: 'Failed to delete the certificate',
      numberingTitle: 'Invoice numbering',
      autoInvoiceNum: 'Automatic KSeF invoice numbering',
      autoInvoiceNumHint: 'When enabled, the invoice number is assigned automatically using the pattern below.',
      prefixLabel: 'Number prefix',
      prefixInvalid: 'The prefix may only contain letters and digits (1-10 characters)',
      prefixPreview: 'Preview: {example}',
      countersTitle: 'Numbering series counters',
      countersHint:
        'Numbering is MONTHLY: each series (prefix + environment) restarts every month from 0001, and every month is a separate row below. By default only the latest month of each series is shown. Setting a counter is useful when migrating from another system — e.g. if a previous KSeF integration already issued invoices EBS/0001..0714, set the counter to 714 so the next invoice gets number 0715, not 0001 (KSeF rejects re-used numbers as duplicates).',
      countersEmpty: 'No counters set yet — the first invoice in a series will start numbering from 0001.',
      counterPrefixColumn: 'Prefix',
      counterEnvironmentColumn: 'Environment',
      counterPeriodColumn: 'Year/month',
      counterLastSeqColumn: 'Last number',
      counterNextColumn: 'Next number preview',
      setCounter: 'Set counter',
      countersShowHistory: 'Show history ({count} older rows)',
      countersHideHistory: 'Hide history',
      counterEnvironmentLabel: 'Environment',
      counterEnvironmentPlaceholder: 'Choose an environment',
      setCounterTitle: 'Set numbering series counter',
      setCounterWarning:
        'A counter can only move forward (the new value must be ≥ the current one) — this guards against accidentally re-issuing a number that was already handed out.',
      counterPrefixInputLabel: 'Series prefix',
      counterYearLabel: 'Year',
      counterMonthLabel: 'Month',
      counterLastSeqLabel: 'Last number already issued',
      counterLastSeqHint: 'The number of the last invoice already issued in this series (e.g. by a previous system) — the next invoice from Periscope will get the number one higher.',
      counterNextPreview: 'Next number preview: {example}',
      setCounterSuccess: 'Counter set',
      setCounterFailed: 'Failed to set the counter',
      counterBackwardError: 'A counter can only move forward — the given value is lower than the current counter state',
      sellerTitle: 'Seller data',
      sellerName: 'Seller name',
      sellerAddressLine1: 'Address — line 1',
      sellerAddressLine2: 'Address — line 2',
      sellerCountryCode: 'Country code (ISO)',
      save: 'Save settings',
      saveSuccess: 'Settings saved',
      saveFailed: 'Failed to save settings',
      tokensTitle: 'API tokens',
      tokensHint: 'Tokens for external integrations with the fiscalization module API.',
      tokensEmpty: 'No API tokens',
      tokenName: 'Name',
      tokenPrefix: 'Prefix',
      tokenNumberingPrefixes: 'Numbering prefixes',
      tokenEnvironment: 'Environment',
      tokenEnvironmentDefault: 'company default',
      tokenEnvironmentLabel: 'KSeF environment',
      tokenEnvironmentHelp:
        'Invoices submitted with this token are issued to the selected environment, regardless of the company setting. The environment is frozen onto each invoice at submit — changing it only affects new invoices.',
      tokenEnvironmentProductionWarning:
        'PRODUCTION: invoices issued with this token will be legally binding and delivered to the national e-invoice system.',
      tokenEnvNoCertificate:
        'No certificate for the {environment} environment — submits from this token will be rejected (400) until one is uploaded.',
      tokenEnvExpiredCertificate:
        'The certificate for the {environment} environment has expired — KSeF delivery will fail until you upload a new one.',
      tokenNumberingPrefixesDefault: 'company default',
      tokenNumberingPrefixesLabel: 'Numbering prefixes',
      tokenNumberingPrefixesPlaceholder: 'e.g. EBS, YVS',
      tokenNumberingPrefixesHelp:
        'Allowed numbering-series prefixes (comma-separated, 1-10 alphanumeric chars each). An invoice picks a series via the numberingPrefix field; omit it → the first prefix in the list; empty list → the company default prefix.',
      tokenPrefixesInvalid: 'Prefixes may contain only letters and digits (1-10 chars each)',
      tokenLastUsed: 'Last used',
      tokenNeverUsed: 'Never used',
      tokenCreated: 'Created',
      addToken: 'New token',
      addTokenTitle: 'New API token',
      editToken: 'Edit token',
      editTokenTitle: 'Edit API token',
      tokenNameLabel: 'Token name',
      tokenNamePlaceholder: 'e.g. ERP integration',
      tokenNameRequired: 'Token name is required',
      createToken: 'Create token',
      tokenCreateSuccess: 'Token created',
      tokenCreateFailed: 'Failed to create the token',
      tokenUpdateSuccess: 'Token updated',
      tokenUpdateFailed: 'Failed to update the token',
      revoke: 'Revoke',
      revokeConfirmTitle: 'Revoke token "{name}"?',
      revokeConfirmMessage: 'Integrations using this token will stop working immediately.',
      revokeSuccess: 'Token revoked',
      revokeFailed: 'Failed to revoke the token',
      tokenRevealTitle: 'API token created',
      tokenRevealDescription: "The fiscalization module API's new access token.",
      tokenRevealWarning: 'This token is shown only once. Copy it now and store it somewhere safe.',
      copyToken: 'Copy token',
      tokenCopied: 'Token copied',
      close: 'Got it, close',
      logoTitle: 'Receipt logo',
      logoHint: 'Logo printed in the header of receipts (e-receipt PDF).',
      logoSet: 'Logo set',
      logoNotSet: 'No logo',
      logoPreviewUnavailable: 'Preview unavailable after a page refresh — the logo is saved.',
      uploadLogo: 'Upload logo',
      replaceLogo: 'Replace logo',
      removeLogo: 'Remove logo',
      logoUploadSuccess: 'Logo uploaded',
      logoUploadFailed: 'Failed to upload the logo',
      logoRemoveSuccess: 'Logo removed',
      logoRemoveFailed: 'Failed to remove the logo',
      logoWrongType: 'Allowed formats: PNG, JPEG',
      logoTooLarge: 'Logo file is too large (max 2MB)',
    },
  },

  // Warehouse (WZ delivery notes, stage 1) — Spec: docs/superpowers/specs/2026-07-21-magazyn-wz-design.md
  magazyn: {
    title: 'Warehouse',
    tabs: {
      wz: 'WZ documents',
      pozycje: 'Items',
    },
    deliveryMethods: {
      courier: 'Courier shipment',
      seller_delivery: 'Seller delivery',
      pickup: 'Self pickup',
    },
    status: {
      issued: 'Issued',
      returned: 'Returned signed',
    },
    list: {
      title: 'WZ documents',
      searchPlaceholder: 'Search by number, buyer or code…',
      statusAll: 'All statuses',
      newWz: 'New WZ',
      uploadSigned: 'Upload signed scan',
      clearFilters: 'Clear filters',
      columns: {
        number: 'Number',
        issueDate: 'Date',
        buyer: 'Buyer',
        deliveryMethod: 'Delivery method',
        trackingNumber: 'Tracking number',
        status: 'Status',
        actions: 'Actions',
      },
      downloadPdf: 'Download PDF',
      downloadFailed: 'Failed to download the PDF',
      edit: 'Edit',
      delete: 'Delete',
      deleteConfirmTitle: 'Delete WZ document {number}?',
      deleteConfirmDescription: 'This action cannot be undone.',
      deleteSuccess: 'WZ document deleted',
      deleteFailed: 'Failed to delete the WZ document',
      empty: {
        title: 'No WZ documents',
        description: 'No documents match the selected filters',
      },
    },
    editor: {
      title: 'WZ document',
      titleNew: 'New WZ document',
      titleEdit: 'Edit WZ document {number}',
      seller: 'Seller',
      buyer: 'Buyer',
      buyerPlaceholder: 'Select a contractor…',
      issueDate: 'Issue date',
      linesTitle: 'Document lines',
      addLine: 'Add line',
      removeLine: 'Remove',
      ordinal: 'No.',
      itemFromCatalog: 'From catalog',
      itemManual: 'Enter manually',
      columns: {
        item: 'Item',
        code: 'Item code',
        description: 'Description / Notes',
        unit: 'Unit',
        quantity: 'Quantity',
      },
      unitDefault: 'pcs.',
      deliveryMethod: 'Delivery method',
      trackingNumber: 'Tracking number',
      trackingNumberRequired: 'Tracking number is required for courier shipments',
      notes: 'Notes',
      save: 'Save',
      saveAndDownload: 'Save and download PDF',
      issueInvoice: 'Issue invoice for WZ',
      fvSoon: 'Coming soon — fiscalization integration',
      savedToast: 'Saved document {number}',
      saveFailed: 'Failed to save the WZ document',
      pdfDownloadFailed: 'Document {number} was saved, but the PDF could not be downloaded',
      returnedBanner: 'Document returned signed — editing is admin-only',
      buyerLockedReturned: 'The buyer cannot be changed once the document is returned',
      quantityInvalid: 'Quantity must be greater than zero',
      linesRequired: 'Add at least one line',
      codeRequired: 'Pick a catalog item or enter a code',
    },
    items: {
      title: 'Items',
      searchPlaceholder: 'Search by code, color or name…',
      addItem: 'Add item',
      editItem: 'Edit item',
      columns: {
        code: 'Code',
        color: 'Color',
        name: 'Name',
        project: 'Project',
        invoiceDescription: 'Invoice description',
        unitPriceNet: 'Net price',
        actions: 'Actions',
      },
      fields: {
        code: 'Code',
        color: 'Color',
        name: 'Name',
        project: 'Project',
        invoiceDescription: 'Invoice description',
        unitPriceNet: 'Net price',
      },
      codeRequired: 'Code is required',
      colorRequired: 'Color is required',
      noProject: 'No project',
      nameRequired: 'Name is required',
      priceInvalid: 'Price must be a number ≥ 0',
      duplicateCode: 'An item with this code already exists',
      deleteConfirmTitle: 'Delete item {code}?',
      deleteConfirmDescription: 'The item will disappear from suggestions; existing WZ documents keep their entered data.',
      deleteSuccess: 'Item deleted',
      deleteFailed: 'Failed to delete the item',
      saveSuccess: 'Item saved',
      saveFailed: 'Failed to save the item',
      empty: {
        title: 'No items in the catalog',
        description: 'Add the first item to enable suggestions on WZ documents',
      },
    },
    upload: {
      title: 'Upload signed scan',
      dropzoneLabel: 'Drag a file here or click to choose',
      dropzoneHint: 'PDF, JPG or PNG',
      submit: 'Upload',
      success: 'Attached to {wzNumber}',
      attachmentCreated: 'The scan was attached to the document',
      needsSelectionTitle: 'Could not match the document automatically',
      needsSelectionDescription: 'Pick the WZ document the scan should be attached to',
      searchPlaceholder: 'Search by WZ number…',
      confirm: 'Attach',
      error: 'Failed to upload the scan',
      foreignToken: "The scan's QR code belongs to a different document, not a WZ of this company",
      wrongType: 'Allowed formats: PDF, JPG, PNG',
    },
  },

  mileage: {
    places: {
      name: 'Name',
      city: 'City',
      distanceMin: 'Distance (km) must be at least 1',
      monthlyMinVisits: 'Min visits/month',
      monthlyMaxVisits: 'Max visits/month',
      visitsRange: '{label} must be between 0 and 20',
      routesFound: 'Found {count} route alternative(s)',
      computeFailed: 'Failed to compute routes',
      editAria: 'Edit place',
      deleteAria: 'Delete place',
    },
    title: 'Vehicle Mileage',
    titleWithCompany: 'Vehicle Mileage: {companyName}',
    subtitle: 'Manage vehicle mileage records for VAT purposes',
    tabs: {
      vehicles: 'Vehicles',
      routes: 'Standard Routes',
      monthly: 'Monthly Log',
    },
    vehicles: {
      title: 'Company Vehicles',
      add: 'Add Vehicle',
      editTitle: 'Edit Vehicle',
      displayName: 'Display Name',
      registrationNumber: 'Registration Number',
      initialOdometer: 'Initial Odometer (km)',
      initialOdometerDate: 'Odometer Date',
      isActive: 'Active',
      notes: 'Notes',
      drivers: 'Drivers',
      addDriver: 'Add Driver',
      driverName: 'Full Name',
      isEmployee: 'Employee',
      isDefault: 'Default',
      noVehicles: 'No vehicles added yet',
    },
    routes: {
      enterAddressesFirst: 'Enter From and To addresses first',
      calculateFailed: 'Failed to calculate distance',
      routesFound: 'Found {count} route(s). {km} km one-way.',
      noPlacesFound: 'No places found',
      searchFailed: 'Failed to search place',
      addressFound: 'Address found: {address}',
      title: 'Standard Routes',
      add: 'Add Route',
      editTitle: 'Edit Route',
      routeFrom: 'From',
      routeTo: 'To',
      distanceKm: 'Distance (km)',
      defaultPurpose: 'Default Purpose',
      isRoundTrip: 'Round Trip',
      calculateDistance: 'Calculate Distance',
      noRoutes: 'No standard routes defined',
    },
    records: {
      odometerStart: 'Odometer Start',
      odometerEnd: 'Odometer End',
      totalKm: 'Total km',
      entriesKm: 'Entries km',
      delta: 'Delta',
      status: {
        draft: 'Draft',
        reconciled: 'Reconciled',
        finalized: 'Finalized',
      },
    },
    entries: {
      add: 'Add Entry',
      edit: 'Edit Entry',
      tripDate: 'Date',
      routeDescription: 'Route',
      purpose: 'Purpose',
      distanceKm: 'km',
      driverName: 'Driver',
      isRoundTrip: 'Round Trip',
    },
    isByCar: 'By Car',
  },

  employees: {
    title: 'Employees',
    titleWithCompany: 'Employees: {companyName}',
    subtitle: 'Manage employees, rates, hours, and contracts for this company',
    addEmployee: 'Add employee',
    noEmployees: 'No employees found',
    noEmployeesDescription: 'There are no employees matching your filters',

    // Types
    type: {
      mandate_contract: 'Umowa zlecenie',
      mandate_contract_student: 'Umowa zlecenie (student)',
      employment_contract: 'Umowa o pracę',
      contractor: 'B2B / Contractor',
      powolanie: 'Board member (appointment)',
      dzielo: 'Umowa o dzieło (specific-task contract)',
      label: 'Type',
      notSet: 'Not set',
    },
    myDataLock: {
      badge: 'Data reveal locked (too many PESEL attempts)',
      unlock: 'Unlock',
      unlocked: 'Data reveal unlocked',
    },
    appAccount: 'App account',

    // Status
    status: {
      active: 'Active',
      expiring_soon: 'Expiring soon',
      expired: 'Expired',
      terminated: 'Terminated',
      label: 'Status',
      all: 'All',
    },

    // Rate
    rateType: {
      flat_monthly: 'Flat monthly',
      hourly: 'Hourly',
      label: 'Rate type',
    },

    // Identity tab
    identity: {
      tab: 'Identity',
      firstName: 'First name',
      lastName: 'Last name',
      pesel: 'PESEL',
      peselHelp: 'Date of birth and sex are derived from the PESEL number',
      dateOfBirth: 'Date of birth',
      sex: 'Sex',
      sexValue: { male: 'Male', female: 'Female' },
      derivedFromPesel: 'from the PESEL number',
      peselOverrodeDateOfBirth:
        'Date of birth was stored from the PESEL ({derived}) instead of the entered value ({submitted})',
      idDocumentType: 'ID document type',
      idDocumentNumber: 'ID document number',
      taxOffice: 'Tax office (Urząd Skarbowy)',
      nfzBranch: 'NFZ branch',
      privateEmail: 'Private email',
      privatePhone: 'Private phone',
      address: 'Address',
      street: 'Street',
      postalCode: 'Postal code',
      city: 'City',
      country: 'Country',
      privateIban: 'Private IBAN',
      privateIbanHelp: 'Used for reimbursement of personal expenses paid for the company',
      notes: 'Notes',
      idDocument: {
        dowod: 'Polish ID',
        passport: 'Passport',
      },
    },

    // Employment tab
    employment: {
      tab: 'Employment',
      type: 'Employment type',
      contractStartDate: 'Contract start',
      contractExpiryDate: 'Contract expiry',
      terminate: 'Terminate',
      terminateConfirm: 'Terminate this employee? They will be archived automatically.',
      archive: 'Archive',
      unarchive: 'Unarchive',
      contractor: {
        title: 'B2B contractor',
        searchPlaceholder: 'Type contractor NIP or pick from list',
        createFromBir: 'Create from BIR ({nip})',
      },
      studentCard: {
        title: 'Student ID card',
        upload: 'Upload student ID',
        replace: 'Replace student ID',
        delete: 'Delete student ID',
        download: 'Download',
        expiryDate: 'Card expiry',
      },
    },

    // Rates tab
    rates: {
      tab: 'Rates',
      addRate: 'Add new rate',
      addRateModal: 'Add rate',
      editRate: 'Edit rate',
      editRateModal: 'Edit rate',
      amount: 'Amount (PLN)',
      validFrom: 'Valid from',
      validTo: 'Valid to',
      current: 'Current',
      noRates: 'No rates set yet',
      delete: 'Delete rate',
      deleteConfirm: 'Delete this rate?',
    },

    // Hours tab
    hours: {
      tab: 'Hours',
      year: 'Year',
      month: 'Month',
      hours: 'Hours',
      monthlyCost: 'Monthly cost',
      months: {
        1: 'January', 2: 'February', 3: 'March', 4: 'April',
        5: 'May', 6: 'June', 7: 'July', 8: 'August',
        9: 'September', 10: 'October', 11: 'November', 12: 'December',
      },
      hourlyOnlyHint: 'Monthly hours apply only when the active rate is hourly',
    },

    // Documents tab
    documents: {
      tab: 'Documents',
      upload: 'Upload document',
      type: 'Document type',
      customLabel: 'Custom label',
      complianceHint: 'This type is tracked in Deadlines & compliance — uploading marks the requirement as fulfilled.',
      types: {
        umowa: 'Contract',
        aneks: 'Contract annex',
        certyfikat: 'Certificate / qualifications',
        zaswiadczenie: 'Attestation',
        other: 'Other',
      },
      label: 'Label',
      fileName: 'File name',
      uploadedAt: 'Uploaded',
      expiry: 'Expiry',
      download: 'Download',
      delete: 'Delete',
      deleteConfirm: 'Delete this document?',
      expiryWarning: 'Expires in {days} days',
      expired: 'Expired',
      signedPublicationBadge: 'Signed document',
      signedConfirmed: 'Confirmed',
      signedAwaitingConfirmation: 'Awaiting confirmation',
      signatureDetails: 'Signature details',
    },

    // Cost report
    costReport: {
      title: 'Cost report',
      titleWithMonth: 'Cost report: {month} {year}',
      pickMonth: 'Month',
      pickYear: 'Year',
      employee: 'Employee',
      monthlyCost: 'Monthly cost',
      totalByType: 'Total by type',
      grandTotal: 'Grand total',
      noActiveRate: 'No active rate',
      missingHours: 'No hours entered',
      exportCsv: 'Export CSV',
    },

    // Payroll (list columns + detail card)
    payroll: {
      tab: 'Payroll',
      gross: 'Gross',
      net: 'Net',
      employerCost: 'Employer cost',
      noRate: 'no rate',
      noType: 'no contract type',
      cardTitle: 'Monthly payroll',
      cardSubtitle: '{month} {year} — from the current rate and employment form',
      rate: 'Rate',
      hours: 'Hours',
      employeeContributions: 'Employee contributions (ZUS)',
      pension: 'Pension (9.76%)',
      disability: 'Disability (1.50%)',
      sickness: 'Sickness (2.45%)',
      contributionsTotal: 'Contributions total',
      health: 'Health contribution (9%)',
      kup: 'Deductible costs (KUP)',
      taxBase: 'Tax base',
      pitAdvance: 'PIT advance',
      ppkEmployee: 'PPK (employee)',
      employerSection: 'Employer cost',
      employerContributionsSection: 'Employer contributions (ZUS)',
      erPension: 'Pension (9.76%)',
      erDisability: 'Disability (6.50%)',
      erAccident: 'Accident insurance',
      erFpFs: 'FP + FS (2.45%)',
      erFgsp: 'FGŚP (0.10%)',
      erPpk: 'PPK (employer)',
      employerContributionsTotal: 'Employer contributions total',
      erTotal: 'Total employer cost',
      assumptions: 'Assumptions',
      notAvailable: 'Payroll unavailable: {reason}',
      loadFailed: 'Failed to compute the payroll breakdown',
    },

    // List filters
    filters: {
      search: 'Search by name, PESEL, email',
      includeArchived: 'Include archived',
    },

    // Validation
    validation: {
      firstNameRequired: 'First name is required',
      lastNameRequired: 'Last name is required',
      typeRequired: 'Employment type is required',
      contractExpiryRequired: 'Contract expiry is required for mandate contracts',
      contractorRequired: 'Contractor must be selected for B2B type',
      studentCardRequired: 'Student card must be uploaded for student type',
      pesel: {
        length: 'Invalid PESEL (must be 11 digits)',
        digits: 'Invalid PESEL (digits only)',
        checksum: 'Invalid PESEL (wrong checksum)',
        date: 'Invalid PESEL (impossible birth date)',
      },
      ibanInvalid: 'Invalid IBAN',
      datesInOrder: 'Start date must be on or before expiry date',
      fileRequired: 'File is required',
      required: '{label} is required',
    },
  },

  theme: {
    toggle: 'Toggle theme',
    light: 'Light',
    dark: 'Dark',
    system: 'System',
  },

  companies: {
    loadFailed: 'Failed to load company details',
    saved: 'Company saved',
    saveFailed: 'Failed to save company',
    bankAccount: {
      ibanRequired: 'Bank account {index}: IBAN is required',
      ibanInvalid: 'Bank account {index}: {error}',
      currencyRequired: 'Bank account {index}: currency is required',
    },
    title: 'Companies',
    subtitle: 'Manage companies in the system',
    addCompany: 'Add company',
    columns: {
      name: 'Name',
      nip: 'NIP',
      invoices: 'Invoices',
      contractors: 'Contractors',
    },
    noCompanies: 'No companies found',
    fetchFromBir: 'Fetch from BIR',
    fetchFromBirSuccess: 'Company data loaded from BIR',
    fetchFromBirFailed: 'Company not found in BIR',
    nip: 'NIP',
    name: 'Name',
    street: 'Street',
    postalCode: 'Postal code',
    city: 'City',
    country: 'Country',
    createSuccess: 'Company created',
    duplicateNip: 'A company with this NIP already exists',
    deleteCompany: 'Delete company',
    deleteWarningTitle: 'Delete company {nip}?',
    deleteWarningBody:
      'This action cannot be undone. All invoices, contractors, tags, employees, vehicles and files associated with this company will be affected.',
    deleteModeUnassign: 'Move invoices to Unassigned, delete the rest',
    deleteModePurge: 'Delete all data (irreversible)',
    confirmNipLabel: 'Retype NIP to confirm',
    confirmNipPlaceholder: 'Enter NIP',
    deleted:
      'Deleted company {nip}: {invoices} invoices, {contractors} contractors, {tags} tags, {employees} employees, {vehicles} vehicles, CRM: {crmAccounts} accounts and {crmLeads} leads',
    deleteS3Warning: 'Some files could not be fully cleaned up and require manual review',
    form: {
      bdoNumber: 'BDO number',
      krsNumber: 'KRS number',
      contactEmail: 'Contact e-mail',
      website: 'Website',
    },
    emailDomains: {
      label: 'Employee e-mail domains',
      hint: 'Entra users from these domains automatically get an employee record',
      placeholder: 'e.g. fss.cc — press Enter to add',
      invalid: 'Invalid domain: {domain}',
    },
    logo: {
      label: 'Company logo',
      hint: 'Shown in the sidebar next to the company name',
      constraints: 'PNG, JPEG or WebP — max 2 MB (SVG is not accepted)',
      dropzone: 'Drop an image here or click to choose a file',
      choose: 'Choose file',
      replace: 'Replace',
      remove: 'Remove',
      alt: 'Logo of {company}',
      uploaded: 'Logo updated',
      removed: 'Logo removed',
      uploadFailed: 'Failed to upload the logo',
      removeFailed: 'Failed to remove the logo',
      tooLarge: 'The file is too large (max 2 MB)',
      wrongType: 'Unsupported format — use PNG, JPEG or WebP',
    },
    representation: {
      label: 'Company representation',
      hint: 'People who sign contracts on behalf of the company — required when generating contract documents',
      mode: 'Representation mode',
      modeNotSet: 'Not configured',
      modeOnePerson: 'One person',
      modeTwoPerson: 'Two persons',
      add: 'Add representative',
      empty: 'No representatives configured',
      person: 'Representative {index}',
      fullName: 'Full name',
      fullNamePlaceholder: 'e.g. Jan Kowalski',
      titleLabel: 'Title',
      titlePlaceholder: 'e.g. Prezes Zarządu',
      fullNameRequired: 'Representative {index}: full name is required',
      titleRequired: 'Representative {index}: title is required',
      twoPersonMin: 'Two-person representation requires at least 2 representatives',
    },
  },

  documents: {
    title: 'Documents',
    templates: {
      title: 'Templates',
      newTemplate: 'New template',
      name: 'Name',
      namePlaceholder: 'Template name',
      description: 'Description',
      descriptionPlaceholder: 'Optional template description',
      category: 'Category',
      categories: {
        employee: 'Employee',
        b2b: 'B2B',
        contractor: 'Contractor',
      },
      visibility: 'Visibility',
      visibilities: {
        public: 'Public',
        private: 'Private (HR only)',
      },
      acceptance: 'Acceptance',
      acceptances: {
        none: 'None',
        electronic: 'Electronic',
        physical: 'Signature',
      },
      audience: 'Audience',
      audiences: {
        all: 'Everyone',
        uop: 'Employment contract',
        uz: 'Mandate contract',
        b2b: 'B2B',
      },
      draft: 'draft',
      version: 'Version',
      updatedAt: 'Updated',
      archived: 'Archived',
      archive: 'Archive',
      unarchive: 'Restore',
      archiveConfirm: 'Archive template {name}?',
      archiveSuccess: 'Template archived',
      unarchiveSuccess: 'Template restored',
      archiveError: 'Failed to update the template',
      created: 'Template created',
      updated: 'Saved',
      createError: 'Failed to create the template',
      loadError: 'Failed to load templates',
      nameRequired: 'Name is required',
      empty: 'No templates',
      templateKind: 'Template type',
      templateKinds: {
        standard: 'Standard',
        umowa_zlecenie: 'Mandate contract (umowa zlecenie)',
      },
      starterHint: 'Or start from a built-in template:',
      starterUmowa: 'From starter: Umowa zlecenie (student)',
      starterCreated: 'Starter template created and published',
      starterError: 'Failed to create the starter template',
    },
    editor: {
      emptyDraft: 'Empty draft cannot be published',
      back: 'Back to list',
      settings: 'Settings',
      preview: 'Preview PDF',
      publish: 'Publish version',
      publishConfirm: 'Publish version v{version}? The version content will be immutable.',
      published: 'Version published',
      history: 'Version history',
      noVersions: 'No published versions',
      variables: 'Variables',
      groups: {
        firma: 'Company',
        pracownik: 'Employee',
        kontrahent: 'Contractor',
        dokument: 'Document',
        wystawca: 'Issuer (logged in)',
        umowa: 'Contract',
        plec: 'Gender forms',
      },
      vars: {
        firma: {
          nazwa: 'Company name',
          nip: 'Company NIP',
          bdo: 'BDO number',
          krs: 'KRS number',
          email: 'Company e-mail',
          www: 'Website',
          ulica: 'Street',
          kod: 'Postal code',
          miasto: 'City',
        },
        pracownik: {
          imie: 'First name',
          nazwisko: 'Last name',
          ulica: 'Street',
          kod: 'Postal code',
          miasto: 'City',
          adres: 'Full address',
          pesel: 'PESEL',
          stanowisko: 'Position',
          typ_umowy: 'Contract type',
          data_zatrudnienia: 'Hire date',
          data_urodzenia: 'Date of birth',
          urzad_skarbowy: 'Tax office',
          rachunek_bankowy: 'Bank account (IBAN)',
        },
        kontrahent: {
          nazwa: 'Contractor name',
          nip: 'Contractor NIP',
          adres: 'Contractor address',
        },
        dokument: {
          data: 'Document date',
          wersja: 'Template version',
          numer: 'Document number',
        },
        wystawca: {
          imie_nazwisko: 'Full name',
          email: 'E-mail',
        },
        umowa: {
          data_zawarcia: 'Conclusion date',
          data_od: 'Contract start',
          data_do: 'Contract end',
          stawka: 'Hourly rate',
          reprezentacja: 'Company representation',
        },
      },
      insertHint: 'Click to insert',
      plecHint:
        "Form picked by the employee's gender (from PESEL). Insert and adjust endings: {{plec:male/female}}",
      saving: 'Saving…',
      saved: 'Saved',
      deadlineDays: 'Default deadline (days)',
      logo: 'Company logo in header',
      unknownVars: 'Unknown variables',
      pagebreakHint: 'Insert [[pagebreak]] on its own line to force a new page',
      modeWysiwyg: 'Editor',
      modeRaw: 'Markdown',
      pagebreakLabel: 'Page break',
      wysiwygLoadError: 'Failed to load the visual editor. Refresh the page or use Markdown mode.',
      toolbar: {
        undo: 'Undo',
        redo: 'Redo',
        h1: 'Heading 1',
        h2: 'Heading 2',
        h3: 'Heading 3',
        paragraph: 'Plain text',
        bold: 'Bold',
        italic: 'Italic',
        strike: 'Strikethrough',
        code: 'Code',
        bulletList: 'Bullet list',
        orderedList: 'Numbered list',
        quote: 'Quote',
        table: 'Table',
        hr: 'Horizontal rule',
        pagebreak: 'Page break',
        addRow: 'Add row (in table)',
        addColumn: 'Add column (in table)',
        deleteRow: 'Delete row',
        deleteColumn: 'Delete column',
        deleteTable: 'Delete table',
      },
    },
    publications: {
      title: 'Publications',
      publish: 'Publish…',
      publishDialogTitle: 'Publish document',
      kind: 'Kind',
      kinds: {
        campaign: 'Campaign',
        individual: 'Individual',
      },
      audience: 'Audience',
      audienceRequired: 'Choose an audience',
      employee: 'Employee',
      employeeRequired: 'Choose an employee',
      employeeSearchPlaceholder: 'Search employee…',
      noEmployees: 'No employees',
      deadline: 'Deadline',
      noDeadline: 'No deadline',
      acceptanceMode: 'Acceptance',
      physicalCampaignHint: 'A physical signature requires an individual publication',
      submit: 'Publish',
      published: 'Published {number}',
      publishError: 'Failed to publish the document',
      number: 'Number',
      recipient: 'Recipient',
      progress: 'Acceptances',
      publishedAt: 'Published',
      empty: 'No publications',
      archived: 'Archived',
      archive: 'Archive',
      archiveConfirm: 'Archive publication {number}? Employees will no longer be reminded.',
      archiveSuccess: 'Publication archived',
      archiveError: 'Failed to archive the publication',
      loadError: 'Failed to load publications',
      detailTitle: 'Publication',
      download: 'Download PDF',
      downloadError: 'Failed to download the document',
      employeeCol: 'Employee',
      status: 'Status',
      statuses: {
        pending: 'Pending',
        returned: 'Returned',
        accepted: 'Accepted',
        signed: 'Signed',
      },
      method: 'Method',
      methods: {
        electronic: 'Electronic',
        physical: 'Signature',
      },
      acceptedAt: 'Date',
      noAcceptances: 'No acceptances required',
      markSigned: 'Mark as signed (scan)',
      markSignedTitle: 'Upload signed scan',
      markSignedFile: 'Scan file',
      markSignedSuccess: 'Signed document saved',
      markSignedError: 'Failed to save the scan',
      attachRevision: 'Upload a newer signed revision',
      attachRevisionTitle: 'Newer revision of the signed document',
      attachRevisionFile: 'PDF file with the additional signature',
      attachRevisionSuccess: 'Newer signed revision saved',
      attachRevisionHint: 'Only a newer revision of THE SAME file is accepted (the same document with an additional signature of a person the document is waiting for — e.g. the PDF countersigned by a representative). A different scan or file goes with the countersign "Sign" button instead.',
      attachRevisionError: 'Failed to save the newer revision',
      attachRevisionCheck: 'Check the file',
      attachRevisionApply: 'Apply this revision',
      attachRevisionAdds: 'This file adds the signatures below. Apply it only if this is the person the document is waiting for — the stored signed file will be replaced by this revision and its signatures will sign the matching countersign rows.',
      attachRevisionPreviewError: 'Failed to check the file',
      confirmReturn: 'Confirm signature',
      confirmReturnSuccess: 'Return confirmed',
      returnedBadge: '{count} to confirm',
      downloadReturned: 'Download signed scan',
      confirmReturnError: 'Failed to confirm the return',
      contract: {
        section: 'Contract data',
        individualHint: 'A contract is always an individual publication',
        conclusionDate: 'Conclusion date',
        dateFrom: 'Period from',
        dateTo: 'Period to',
        hourlyRate: 'Gross hourly rate (PLN/h)',
        representation: 'Company representation',
        modes: {
          one_person: 'One-person representation',
          two_person: 'Two-person representation',
        },
        selectExactly: 'select {count} signer(s)',
        orderHint: 'Selection order determines the signing order (1. signs first)',
        notConfigured:
          'Company representation is not configured — set the mode and representatives in the company settings before publishing this contract',
        loadFailed: 'Failed to load the company representation — publishing is blocked until it loads',
        retry: 'Retry',
        writeRate: "Save the rate in the employee's records",
        datesRequired: 'Conclusion date and contract period are required',
        dateOrder: 'The period start cannot be after its end',
        rateRequired: 'Enter a valid hourly rate',
        rateFormat: 'The rate must be greater than 0, with at most 2 decimal places',
        rateWarning: 'Published, but the rate was not saved in the records: {message}',
      },
      countersigns: {
        title: 'Representative signatures',
        fullySigned: 'Fully signed',
        statuses: {
          waiting: 'Waiting',
          pending: 'Awaiting signature',
          signed: 'Signed',
        },
        sign: 'Sign',
        signTitle: 'Representative signature — {name}',
        signFile: 'Signed file (optional)',
        signFileHint:
          'Attach a digitally signed PDF or a scan; leave empty when signed on paper only',
        signSuccess: 'Signature recorded',
        signError: 'Failed to record the signature',
        downloadFile: 'Download signed file',
      },
    },
  },

  errors: {
    loadingFailed: 'Failed to load data',
    saveFailed: 'Failed to save',
    deleteFailed: 'Failed to delete',
    accessDenied: 'Access denied',
    notFound: 'Not found',
    pdfFailed: 'Failed to generate PDF',
    sessionExpired: 'Session Expired',
    sessionExpiredMessage: 'Your session has expired. Please sign in again to continue.',
    signInAgain: 'Sign in again',
    signInFailed: 'Could not start sign-in. Reload the page and try again.',
    reloadPage: 'Reload page',
  },

  auth: {
    tabMicrosoft: 'Microsoft',
    tabPassword: 'Password',
    email: 'E-mail',
    password: 'Password',
    signIn: 'Sign in',
    signInWithMicrosoft: 'Sign in with Microsoft',
    microsoftHint: 'Access requires membership in an authorized group',
    invalidCredentials: 'Invalid e-mail or password',
    tooManyAttempts: 'Too many attempts. Try again in 15 minutes.',
    loginFailed: 'Sign-in failed. Please try again.',
    turnstileRequired: 'Please wait for the anti-bot verification',
    turnstileFailed: 'Anti-bot verification failed. Please try again.',
    verificationUnavailable:
      'Sign-in verification is temporarily unavailable. Please try again in a moment.',
    changePasswordTitle: 'Set a new password',
    changePasswordSubtitle: 'You signed in with a temporary password. Set a new one to continue.',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    passwordMinLengthHint: 'At least {min} characters',
    passwordTooShort: 'Password must be at least {min} characters long',
    passwordMismatch: 'Passwords do not match',
    wrongCurrentPassword: 'Current password is incorrect',
    changePasswordFailed: 'Failed to change password. Please try again.',
    changePasswordSubmit: 'Change password and sign in',
    // Sonar scope instrument chatter (login background scene)
    scopeStation: 'PERISCOPE DEPTH',
    scopeDepth: 'DPT {m} M',
    scopeBearing: 'BEARING {deg}°',
    scopeContact: 'CONTACT IDENTIFIED · BEARING 047',
    scopeRetry: 'RE-CHECK BEARING',
  },

  profile: {
    apiToken: {
      menuItem: 'API token',
      title: 'API token',
      description:
        'Personal token for uploading invoices via the API (e.g. an iOS Shortcut). Send it to POST /invoice/upload as an "Authorization: Bearer …" or "X-Api-Key" header.',
      noToken: 'You have no API token yet.',
      tokenLabel: 'Token',
      created: 'Created',
      lastUsed: 'Last used',
      neverUsed: 'never',
      generate: 'Generate token',
      rotate: 'Rotate token',
      rotateConfirm:
        'Rotating replaces your current token — anything still using the old one will stop working. Continue?',
      revoke: 'Revoke token',
      revokeConfirm: 'Revoke the token? Anything using it will stop working.',
      newTokenTitle: 'Your new token',
      newTokenWarning: 'Copy it now — for security reasons it will not be shown again.',
      copy: 'Copy',
      copied: 'Token copied to clipboard',
      loadFailed: 'Failed to load API token status',
      generateFailed: 'Failed to generate API token',
      revokeFailed: 'Failed to revoke API token',
      revoked: 'API token revoked',
    },
  },

  apiApps: {
    title: 'API applications',
    description:
      'Global tokens for external applications (pap_…). Registry lookups only — never invoices or receipts.',
    addApp: 'Add application',
    name: 'Name',
    descriptionLabel: 'Description',
    scopes: 'Scopes',
    scopeCompanyPl: 'White List — Polish NIP (company:lookup:pl)',
    scopeCompanyEu: 'VIES — EU VAT id (company:lookup:eu)',
    scopeCrmLeads: 'CRM — inbound leads (crm:leads:write)',
    companyNip: 'Company',
    companyNipHint:
      'Required for the CRM scope — leads land in the CRM of the chosen company. "Global token" = lookups only.',
    companyGlobal: 'Global token (lookups only)',
    companyOutOfList: '(not in list)',
    companyNipRequired: 'The CRM scope requires choosing a company',
    expiresAt: 'Expires',
    neverExpires: 'Never expires',
    expired: 'Expired',
    lastUsed: 'Last used',
    neverUsed: 'Never',
    created: 'Created',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    tokenPrefix: 'Token',
    createTitle: 'New API application',
    editTitle: 'Edit application',
    nameRequired: 'Name is required',
    scopesRequired: 'Select at least one scope',
    createSuccess: 'Application created',
    updateSuccess: 'Changes saved',
    deleteSuccess: 'Application deleted',
    regenerate: 'Generate a new token',
    regenerateSuccess: 'Token regenerated',
    regenerateConfirmTitle: 'Generate a new token for "{name}"?',
    regenerateConfirmMessage:
      'The current token stops working immediately. The new token is shown only once.',
    tokenRevealTitle: 'Application token',
    tokenRevealDescription: 'Copy the token now — it will never be shown again.',
    tokenRevealWarning:
      'The token is displayed only once. After closing this dialog it can only be regenerated.',
    copyToken: 'Copy',
    tokenCopied: 'Token copied',
    deleteConfirmTitle: 'Delete application "{name}"?',
    deleteConfirmMessage: 'The token stops working immediately. This cannot be undone.',
    delete: 'Delete',
    close: 'Close',
    empty: 'No API applications',
  },

  users: {
    title: 'Users',
    subtitle: 'Manage accounts and permissions',
    addUser: 'Add user',
    syncDirectory: 'Sync from Entra',
    syncDirectorySuccess: 'Synced {updated} of {scanned} users, {members} directory members scanned, {employees} employee records created',
    syncDirectoryStale: '{stale} directory-sourced employees are no longer in Entra — review them in HR',
    syncDirectoryUnavailable: 'Entra directory unavailable — check the app permissions (User.Read.All)',
    syncDirectoryRunning: 'A sync is already running',
    syncDirectoryFailed: 'Failed to sync the Entra directory',
    noUsers: 'No users found',
    noRoles: 'No roles',
    permissions: 'Permissions',
    columns: {
      email: 'E-mail',
      name: 'Name',
      source: 'Source',
      roles: 'Roles',
      status: 'Status',
    },
    source: {
      entra: 'Microsoft',
      local: 'Local',
    },
    status: {
      active: 'Active',
      inactive: 'Inactive',
    },
    roleNames: {
      ksiegowy: 'Accountant',
      admin: 'Administrator',
      user: 'User',
      pracownik: 'Employee',
      hr: 'HR',
      crm: 'CRM (sales)',
    },
    moduleNames: {
      dashboard: 'Dashboard',
      expense: 'Cost invoices',
      sales: 'Sales',
      projects: 'Projects (statistics)',
      contractors: 'Contractors',
      magazyn: 'Warehouse',
      mileage: 'Mileage',
      employees: 'Employees',
      tags: 'Tags',
      hr: 'HR',
      fiscal: 'Fiscalization',
      crm: 'CRM',
    },
    form: {
      email: 'E-mail',
      displayName: 'Display name',
      company: 'Company',
      role: 'Role',
      modules: 'Modules',
      tagsForModule: 'Restrict visibility to tags ({module})',
      sendInvite: 'Send an e-mail invitation',
      selectCompany: 'Select a company',
      selectRole: 'Select a role',
      noTagsForModule: 'No tags defined for this module',
      accountType: 'Account type',
      providerEntra: 'Microsoft (Entra ID)',
      providerLocal: 'Local account (password)',
      entraHint:
        'The user signs in with their Microsoft account — pick them from the Entra directory. Permissions apply automatically on first login, with no password or invite.',
      sendEntraNotice: 'Send an access notification email',
      directoryUser: 'Person from the Entra directory',
      searchDirectory: 'Pick a person from the directory…',
      searchDirectoryPlaceholder: 'Search: name or e-mail…',
      searchDirectoryHint: 'Type at least 2 characters',
      directoryNoResults: 'No matches in the directory',
      directoryUnavailable: 'Entra directory unavailable — enter the e-mail address manually',
      manager: 'Manager',
      managerLoading: 'Loading manager details…',
      noManager: 'no manager',
    },
    directory: {
      title: 'Entra directory data',
      jobTitle: 'Job title',
      department: 'Department',
      manager: 'Manager',
      noManager: 'no manager',
      syncedAt: 'Last synced',
      neverSynced: 'never synced',
    },
    createSuccess: 'User created',
    createdNoInvite: 'User created. Share the temporary password below with them.',
    temporaryPassword: 'Temporary password',
    copyPassword: 'Copy',
    passwordCopied: 'Password copied to clipboard',
    close: 'Close',
    drawer: {
      title: 'User permissions',
      active: 'Active',
      activeHint: 'An inactive user cannot sign in',
      rolesInCompany: 'Roles',
      addRole: 'Add role',
      grants: 'Module access',
      roleAdded: 'Role added',
      roleRemoved: 'Role removed',
      unassignedTitle: 'No company (employee placeholder)',
      addUnassigned: 'Grant unassigned employee role',
      userUpdated: 'User updated',
      addCompany: 'Add company',
      searchCompany: 'Search companies…',
      noCompanyResults: 'No company found',
      pickRole: 'Pick a role',
      noAccess: 'This user has no company access yet',
      removeAccess: 'Remove access',
      removeAccessConfirmTitle: 'Remove company access?',
      removeAccessConfirmMessage:
        'The user will lose all roles and module access in {company}.',
      accessRemoved: 'Access removed',
      fullAccess: 'Full access to all modules (from the role)',
      hrOnlyAccess: 'HR module access (from the role)',
      grantsOnlyForUserRole: 'Modules only apply to the "User" role',
    },
    errors: {
      createFailed: 'Failed to create user',
      addRoleFailed: 'Failed to add role',
      removeRoleFailed: 'Failed to remove role',
      saveGrantsFailed: 'Failed to save permissions',
      removeAccessFailed: 'Failed to remove access',
      updateFailed: 'Failed to update user',
    },
  },

  notifications: {
    title: 'Notifications',
    empty: 'No notifications',
    markAllRead: 'Mark all as read',
    justNow: 'just now',
    minutesAgo: '{m} min ago',
    hoursAgo: '{h} h ago',
    earlyPaymentRequest: '{name} asks for an earlier transfer for invoice {invoice} — proposed date: {date}',
    absenceReported: '{name} reports: {type} {from} – {to}',
    absenceRequested: '{name} filed a request: {type} {from} – {to}',
    fundsNotReceivedInvoice: '⚠ {name} reports missing funds for invoice {invoice}',
    fundsNotReceived: '⚠ {name} reports a missing payout',
    reimbursementRequested: '{name} requests a {amount} PLN reimbursement for {invoice}',
    reimbursementApproved: 'Your {amount} PLN reimbursement for {invoice} was approved',
    reimbursementRejected: 'Your reimbursement for {invoice} was rejected: {reason}',
    myDataLocked: '🔒 {name}: personal-data reveal locked after too many PESEL attempts — possible session hijack',
    b2bAccountNotOnWhitelist: "B2B verification: {contractor} — invoice {invoice} bank account is NOT on the VAT White List",
    b2bVatStatusNegative: 'B2B verification: {contractor} has a negative VAT registry status',
    b2bVerificationUnavailable: 'B2B verification: {contractor} could not be verified (government API unavailable)',
    documentPublished: 'New document to review: {title}',
    documentAcceptanceReminder: 'Document {title} awaits acceptance (deadline {deadline})',
    documentAcceptanceReminderNoDeadline: 'Document {title} awaits acceptance',
    documentOverdue: '{employeeName} has not accepted the document {title} by the deadline',
    documentReturned: 'Signed copy returned: {title} ({file}) — awaiting confirmation',
    documentReturnedSignedBy:
      'Signed copy returned: {title} ({file}) — signed by {name}, awaiting confirmation',
    documentReturnSuperseded:
      'Newer signed revision returned: {title} ({file}) — parked for review, the stored file was NOT replaced automatically',
    documentReturnSupersededSignedBy:
      'Newer signed revision returned: {title} ({file}) — additional signature: {name}; parked, awaiting operator decision',
    documentReturnNeedsReview:
      'A return for the completed document {title} ({file}) needs review — nothing was changed',
    documentReturnFailed:
      'A returned document could not be stored: {title} ({file}) — it must be sent again',
    documentCountersignPending:
      'Document "{title}" ({number}) awaits a representative signature: {representativeName}',
    documentFullySigned:
      'Contract "{title}" ({number}) — {employeeName} — has been signed by all parties',
    fiscalInvoiceRejected: 'Fiscal invoice {number} was rejected by KSeF',
    fiscalInvoiceRejectedWithError: 'Fiscal invoice {number} was rejected by KSeF: {error}',
    fiscalReceiptRejected: 'Fiscal receipt on printer {printer} was rejected',
    fiscalReceiptRejectedWithError: 'Fiscal receipt on printer {printer} was rejected: {error}',
    fiscalPrinterOffline: 'Fiscal printer {printer} is offline',
    fiscalDailyReportFailed: 'Daily fiscal report for {printer} ({date}) failed',
    fiscalDailyReportFailedWithError: 'Daily fiscal report for {printer} ({date}) failed: {error}',
    fiscalDailyReportOverdue:
      'Missing daily fiscal report for {date} — printer {printer}. Check the printer/fiscal agent.',
    crmLeadReceived: 'New lead: {name} (source: {source})',
    crmLeadReceivedCompany: 'New lead: {name}, {company} (source: {source})',
    unknownContractor: 'unknown contractor',
    contractorDocumentExpiringOne: 'Document "{label}" ({contractor}) expires in 1 day',
    contractorDocumentExpiringSoon: 'Document "{label}" ({contractor}) expires in {days} days',
    contractorDocumentExpiringToday: 'Document "{label}" ({contractor}) expires today',
    contractorDocumentExpiredOne: 'Document "{label}" ({contractor}) expired 1 day ago',
    contractorDocumentExpired: 'Document "{label}" ({contractor}) expired {days} days ago',
  },
  hr: {
    title: 'HR',
    tabs: {
      me: 'My leave',
      team: 'Team',
      admin: 'HR',
      invoices: 'My invoices',
      reimbursements: 'Reimbursements',
      myData: 'My data',
      documents: 'Documents',
    },
    myData: {
      revealLockWarning: 'After 5 incorrect attempts, access to your sensitive data is locked and HR is alerted — you will then need HR or an admin to unlock it.',
      title: 'My data',
      readOnlyHint: 'Only HR can edit this data — report changes to your HR contact.',
      sectionPersonal: 'Personal data',
      sectionContact: 'Contact',
      sectionAddress: 'Address',
      sectionEmployment: 'Employment',
      sectionSensitive: 'Documents & account',
      sectionPayroll: 'Pay',
      firstName: 'First name',
      lastName: 'Last name',
      dateOfBirth: 'Date of birth',
      bornOn: 'born {date}',
      sex: 'Sex',
      sexMale: 'Male',
      sexFemale: 'Female',
      privateEmail: 'Private e-mail',
      privatePhone: 'Phone',
      workEmail: 'Work e-mail',
      street: 'Street',
      postalCode: 'Postal code',
      city: 'City',
      country: 'Country',
      employmentType: 'Employment type',
      contractStart: 'Contract start',
      contractExpiry: 'Contract end',
      jobTitle: 'Job title',
      department: 'Department',
      manager: 'Manager',
      fte: 'FTE',
      dailyNorm: 'Daily norm',
      taxOffice: 'Tax office',
      nfzBranch: 'NFZ branch',
      pesel: 'PESEL',
      idDocument: 'ID document',
      idDowod: 'ID card',
      idPassport: 'Passport',
      iban: 'Bank account',
      reveal: 'Reveal sensitive data',
      revealTitle: 'Confirm your identity',
      revealHint: 'Enter the last 4 digits of your PESEL to reveal sensitive data for 5 minutes.',
      revealInput: 'Last 4 digits of PESEL',
      revealSubmit: 'Reveal',
      revealNoPesel: 'No PESEL on file — contact HR to reveal your data.',
      revealLocked: 'Revealing is locked after too many failed attempts — ask HR or an admin to unlock it.',
      hideNow: 'Hide now',
      revealedFor: 'Data revealed — hides automatically in {s} s',
      payrollGross: 'Gross',
      payrollZus: 'ZUS contributions (employee)',
      payrollZusShort: 'ZUS',
      payrollHealth: 'Health contribution',
      payrollHealthShort: 'Health',
      payrollPit: 'PIT advance',
      payrollPitShort: 'PIT',
      payrollPpk: 'PPK (employee)',
      payrollPpkShort: 'PPK',
      payrollNet: 'Net',
      payrollEmployerCost: 'Total employer cost',
      payrollRate: 'Rate',
      payrollUnavailable: 'Pay cannot be computed (no rate or employment type).',
      perMonth: '/mo',
      perHour: '/h',
    },
    myInvoices: {
      title: 'My invoices',
      empty: 'No invoices linked to your company',
      colNumber: 'Invoice no.',
      colReceived: 'Received',
      colDue: 'Payment due',
      colAmount: 'Amount',
      colStatus: 'Status',
      status: {
        received: 'Received',
        passed_for_payment: 'Passed for payment',
        paid: 'Paid',
      },
      termInfo: 'Payment term: 14 days from invoice receipt (per contract).',
      earlyPayment: 'Request earlier transfer',
      earlyPaymentTitle: 'Earlier transfer — invoice {invoice}',
      earlyPaymentDate: 'Proposed transfer date',
      earlyPaymentNote: 'Note (optional)',
      earlyPaymentSend: 'Send request',
      earlyPaymentSent: 'Request sent — admins and your manager were notified',
      fundsAlarm: 'Funds not received',
      fundsAlarmConfirm: 'Report missing funds for invoice {invoice}? Company admins and your manager will be alerted.',
      fundsAlarmSent: 'Alarm sent',
    },
    payAlarm: {
      button: 'I did not receive my pay',
      confirm: 'Report a missing payout? Company admins and your manager will be alerted.',
      sent: 'Alarm sent',
    },
    reimbursements: {
      approveConfirmTitle: 'Approve reimbursement?',
      approveConfirmBody: 'This will approve a payout of {amount} to {employee} and mark the invoice paid. This cannot be undone.',
      title: 'Expense reimbursements',
      request: 'Request an invoice reimbursement',
      empty: 'No reimbursement requests',
      approvalsTitle: 'Awaiting your approval',
      myTitle: 'My requests',
      colInvoice: 'Invoice / receipt',
      colVendor: 'Issuer',
      colEmployee: 'Employee',
      colAmount: 'Reimbursement',
      colStatus: 'Status',
      colRequested: 'Requested',
      status: {
        pending_approval: 'Awaiting approval',
        approved: 'Approved — awaiting transfer',
        rejected: 'Rejected',
        exported: 'Transfer ordered',
      },
      searchTitle: 'Find your invoice / receipt',
      searchHintNip: 'Enter the issuer NIP and the gross amount (Polish invoices).',
      searchNip: 'Issuer NIP',
      searchGross: 'Gross amount',
      searchByDate: 'Not found? Search by issue date',
      searchIssueDate: 'Issue date',
      searchHintDate: 'For foreign documents (e.g. EUR): issue date + gross amount, no NIP.',
      searchButton: 'Search',
      searchEmpty: 'No matching document found. If it is a scan — e-mail it to periscope@fssrke3.com (or upload it in Costs) first and retry in a few minutes.',
      confirmTitle: 'Confirm the reimbursement request',
      reimbursedAmount: 'Reimbursement amount (PLN)',
      fxNote: 'Foreign-currency document — converted at the NBP rate of the day before issue.',
      ibanLabel: 'Transfer to account',
      submit: 'Send request',
      submitted: 'Request sent — your manager and admins were notified',
      approve: 'Approve',
      reject: 'Reject',
      rejectTitle: 'Reject the request',
      rejectReason: 'Rejection reason',
      approved: 'Request approved',
      rejected: 'Request rejected',
      rejectedReason: 'Reason: {reason}',
    },
    notLinked: {
      title: 'No employee record linked',
      body: 'Your account is not linked to any employee record in this company, so there is no leave balance, calendar or request to show.',
      action: 'If you work at this company, ask HR or an admin to check your employee record — it needs your work e-mail address. The link is then created automatically (at your next sign-in, or after the nightly directory sync).',
      adminHint: 'As HR/admin: open the HR tab, check that your own record carries the right work e-mail, then run Settings → Users → Sync directory. Accounts are linked to records by Entra object id (OID); e-mail is only the fallback match for records entered by hand.',
    },
    types: {
      vacation: 'Annual leave',
      vacation_on_demand: 'On-demand leave',
      unpaid: 'Unpaid leave',
      occasional: 'Special leave',
      childcare_art188: 'Childcare (art. 188)',
      care_art173: 'Carer’s leave',
      force_majeure_art148: 'Force majeure',
      sick: 'Sick leave',
      unavailability_b2b: 'Unavailability',
      remote_work: 'Remote work',
    },
    typesShort: {
      vacation: 'Leave',
      vacation_on_demand: 'On-demand',
      unpaid: 'Unpaid',
      occasional: 'Special',
      childcare_art188: 'Childcare',
      care_art173: 'Carer',
      force_majeure_art148: 'F. majeure',
      sick: 'Sick',
      unavailability_b2b: 'Unavail.',
      remote_work: 'Remote',
    },
    status: {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
    },
    balance: {
      remainingLabel: 'Annual leave remaining in {year}',
      used: 'Used',
      pending: 'Pending',
      remaining: 'Remaining',
      entitlement: 'Entitlement',
      entitlementValue: '{base} + {extra}',
      onDemand: 'On-demand leave',
      onDemandOf: '{used}/{cap} days',
      sickDaysYtd: 'Sick days this year',
      overdue: 'Overdue leave',
      overdueWarning: '{amount} overdue — use by {deadline} (art. 168 KP)',
      noOverdue: 'No overdue leave',
      request: 'Request leave',
      b2bTitle: 'B2B / mandate cooperation',
      b2bBody: 'Statutory annual leave does not apply to your form of engagement. Report unavailability instead — your manager is notified automatically, no approval needed.',
      noContractType: 'Your contract type has not been configured by HR yet. Until then you can report unavailability or remote work.',
      unavailabilityYtd: 'Unavailability this year',
      remoteYtd: 'Remote days this year',
      reportUnavailability: 'Report unavailability',
    },
    calendar: {
      hint: 'Drag across days to file a request — weekends and public holidays never consume leave.',
      today: 'Today',
      prevMonth: 'Previous month',
      nextMonth: 'Next month',
      legendWeekend: 'Weekend / public holiday',
      legendPending: 'pending',
    },
    request: {
      title: 'New leave request',
      titleB2b: 'Report unavailability',
      type: 'Absence type',
      groupPool: 'Annual leave pool',
      groupOther: 'Other absences',
      groupInfo: 'Notifications',
      dateFrom: 'From',
      dateTo: 'To',
      halfDay: 'Half day ({hours} h)',
      halfDayStart: 'Half day — first day',
      halfDayEnd: 'Half day — last day',
      reason: 'Reason (optional)',
      reasonOccasionalHint: 'e.g. wedding, birth of a child, funeral',
      workingDays: 'Working days',
      workingDaysValue: '{days} d ({hours} h)',
      freeDaysExcluded: '{count} weekend/holiday day(s) in this range cost nothing',
      afterApproval: 'Left after approval',
      onDemandCounter: 'On-demand used this year',
      overlapWarning: 'Overlaps your existing request ({range})',
      errors: {
        crossYear: 'A request must fit in one calendar year — split it at 31 December.',
        insufficient: 'This request exceeds your remaining leave by {amount}.',
        onDemandCap: 'On-demand leave is capped at 4 days per calendar year (art. 167² KP).',
        noWorkingDays: 'The selected range contains only weekends/public holidays.',
      },
      hints: {
        occasional: 'Up to 2 working days per family event — wedding, birth, funeral (§15 rozp. MPiPS).',
        childcare_art188: '2 days per calendar year, fully paid (art. 188 KP).',
        care_art173: 'Up to 5 unpaid days per year to care for a family member (art. 173¹ KP).',
        force_majeure_art148: '2 days per year at 50% pay, for urgent family matters (art. 148¹ KP).',
        unpaid: 'Does not consume the leave pool (art. 174 KP).',
        remote_work: 'Informational only — shows on the calendar, needs no approval.',
        unavailability_b2b: 'Notification only — your manager is informed, no approval flow.',
      },
      submit: 'Submit request',
      submitInfo: 'Save notification',
      cancel: 'Cancel',
      successTitle: 'Request submitted',
      successInfoTitle: 'Notification saved',
      successBalance: 'After approval you will have {amount} left',
    },
    myRequests: {
      title: 'My requests',
      empty: 'You have no requests yet — drag across days in the calendar to file one.',
      showAll: 'Show all ({count})',
      showLess: 'Show less',
      cancel: 'Cancel request',
      cancelTitle: 'Cancel this request?',
      cancelBody: '{type}, {range}. This cannot be undone.',
      cancelConfirm: 'Cancel request',
      cancelKeep: 'Keep it',
      cancelled: 'Request cancelled',
      decisionNote: 'Note: {note}',
    },
    whosOut: {
      title: 'Who’s out — next 7 days',
      empty: 'Nobody on your team is away in the next 7 days.',
      pendingSuffix: '(pending)',
    },
    team: {
      queueTitle: 'Approval queue',
      queueEmpty: 'No pending requests — you’re all caught up.',
      afterApproval: 'After approval: {amount} left',
      conflict: '{count} of {total} team members are away in this period: {names}',
      approve: 'Approve',
      reject: 'Reject',
      note: 'Add a note',
      notePlaceholder: 'Optional note for the employee…',
      approved: 'Request approved',
      rejected: 'Request rejected',
      actionFailed: 'Action failed',
      calendarTitle: 'Team calendar',
      workingDaysShort: '{days} working',
    },
    grid: {
      month: 'Month',
      quarter: 'Quarter',
      department: 'Department',
      allDepartments: 'All departments',
      type: 'Type',
      allTypes: 'All types',
      allStatuses: 'Pending + approved',
      approvedOnly: 'Approved only',
      empty: 'No absences in this period.',
      showMore: 'Show {count} more',
      people: '{count} people',
    },
    admin: {
      tabs: {
        calendar: 'Company calendar',
        register: 'Leave register',
        sick: 'Sick leave (e-ZLA)',
        stats: 'Statistics',
        payroll: 'Payroll calculator',
        compliance: 'Deadlines & compliance',
      },
    },
    compliance: {
      title: 'My documents & deadlines',
      queueTitle: 'Documents awaiting verification',
      adminTitle: 'Deadlines & compliance',
      types: {
        student_card: 'Student ID card',
        medical_exam: 'Medical examination',
        bhp_training: 'H&S (BHP) training',
        contract_expiry: 'Contract end',
        rud_report: 'ZUS RUD notification (umowa o dzieło)',
      },
      status: {
        missing: 'Missing',
        pending_verification: 'Awaiting verification',
        valid: 'Valid',
        expired: 'Expired',
        rejected: 'Rejected',
      },
      validUntil: 'Valid until {date}',
      contractUntil: 'Contract until {date}',
      daysLeft: '{days} days left',
      daysOverdue: '{days} days overdue',
      dueToday: 'last day of validity',
      rejectedReason: 'Reason: {reason}',
      submittedAt: 'Submitted {date}',
      upload: 'Add document',
      uploadAgain: 'Add again',
      uploadTitle: 'Add: {type}',
      uploadHint: 'PDF or photo (JPG/PNG/WebP/HEIC), max 10 MB. Your manager will verify the document.',
      employerManagedHint: 'Arranged by the employer',
      recordDocument: 'Add document',
      recordHint: 'PDF or photo (JPG/PNG/WebP/HEIC), max 10 MB. The document goes to the employee file and is valid immediately — no verification.',
      recordSubmit: 'Save document',
      recordSuccess: 'Document saved to the employee file',
      uploadFile: 'File',
      uploadValidUntil: 'Document valid until',
      uploadSubmit: 'Send for verification',
      uploadSuccess: 'Document sent for verification',
      empty: 'No documents are required for your employment form.',
      queueEmpty: 'No documents awaiting verification.',
      preview: 'Preview',
      verify: 'Verify',
      verified: 'Document verified',
      rejectAction: 'Reject',
      rejectTitle: 'Reject document',
      rejectBody: '{type} — {employee}. The employee will receive an e-mail with the reason.',
      rejectReasonLabel: 'Rejection reason',
      rejectReasonPlaceholder: 'e.g. unreadable scan, missing validity date',
      rejectConfirm: 'Reject document',
      rejectedToast: 'Document rejected',
      actionFailed: 'Action failed',
      filterType: 'Type',
      filterStatus: 'Status',
      filterDepartment: 'Department',
      filterAll: 'All',
      exportCsv: 'Export CSV',
      colEmployee: 'Employee',
      colDepartment: 'Department',
      colType: 'Requirement',
      colStatus: 'Status',
      colValidUntil: 'Valid until',
      colDaysLeft: 'Days',
      colDocument: 'Document',
      rudMarkDone: 'Mark as filed',
      rudMarkedDone: 'RUD notification marked as done',
    },
    payroll: {
      title: 'Payroll calculator (2026)',
      subtitle: 'Gross ↔ net and the total employer cost under Polish rules',
      form: 'Employment form',
      forms: {
        uop: 'Employment contract (UOP)',
        zlecenie: 'Mandate contract (zlecenie)',
        zlecenie_student: 'Zlecenie — student under 26',
        b2b: 'B2B (invoice)',
        powolanie: 'Board member (appointment)',
        dzielo: 'Umowa o dzieło (specific-task contract)',
      },
      modeGross: 'From gross',
      modeNet: 'From net',
      amountGross: 'Gross amount (PLN/mo)',
      amountNet: 'Net amount — take-home (PLN/mo)',
      options: 'Options',
      pit2: 'PIT-2 (300 zł/mo tax-reducing amount)',
      ppk: 'PPK (employee 2%, employer 1.5%)',
      kup300: 'Elevated KUP 300 zł (out-of-town commuter)',
      under26: 'Under 26 (young-person relief)',
      sickness: 'Voluntary sickness contribution',
      kupAutorskie: '50% KUP (authors’ rights)',
      kupAutorskieDzielo: 'Transfer of copyright — przeniesienie praw autorskich (50% KUP)',
      ownEmployer: 'Contract with one’s own employer (full ZUS as for an employment contract)',
      emptyState: 'Enter an amount to see the breakdown',
      invalidAmount: 'Enter a valid amount',
      calcFailed: 'Calculation failed',
      resultGross: 'Gross',
      resultNet: 'Net (take-home)',
      resultEmployerCost: 'Employer cost',
      b2bNote: 'B2B: the company cost is the invoice net amount — the contractor settles their own ZUS and taxes.',
      dzieloNote: 'Umowa o dzieło: no ZUS or health contributions — company cost = gross. Remember to file the ZUS RUD notification within 7 days of conclusion (not required for a contract with your own employee).',
    },
    register: {
      year: 'Year',
      employee: 'Employee',
      contract: 'Contract',
      fte: 'FTE',
      seniority: 'Seniority',
      seniorityHelp: 'Pre-hire leave seniority: previous employers plus the single most favourable education credit (basic vocational 3y, secondary 4–5y, post-secondary 6y, university 8y — art. 155 KP; credits never sum). Tenure at this company is added automatically from the contract start date. 20 days below 10 total years, 26 days from 10.',
      calc: {
        open: 'Seniority calculator (art. 155 KP)',
        title: 'Leave-seniority calculator',
        education: 'Completed school (highest)',
        employment: 'Years at previous employers (outside the study period)',
        rules: 'Education credits never sum — only the single most favourable school counts (art. 155 §1 KP). Studying while employed: the more favourable period counts, not both (art. 155 §2 KP). Do NOT enter tenure at this company — it is added automatically from the contract start date.',
        suggestion: 'Suggested seniority',
        years: 'years',
        daysSuffix: 'leave days',
        apply: 'Apply',
        edu: {
          none: 'None / not applicable',
          zawodowa: 'Basic vocational school',
          liceum: 'General secondary school',
          technikum: 'Vocational secondary (technikum)',
          policealna: 'Post-secondary school',
          wyzsza: 'University (BA/BSc/MA)',
        },
      },
      entitlement: 'Entitlement',
      carryOver: 'Carried over',
      adjustment: 'Adjustment',
      used: 'Used',
      pendingCol: 'Pending',
      remaining: 'Remaining',
      overdue: 'Overdue',
      overdueBy: 'Use by {deadline} (art. 168 KP)',
      firstJobShort: '1st job',
      notConfigured: 'not set',
      noPool: 'No statutory leave',
      uopOnly: 'Editable for employment contracts (UOP) only',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      saved: 'Leave configuration saved',
      exportCsv: 'Export CSV',
      empty: 'No employees.',
    },
    sick: {
      title: 'Sick-leave register',
      add: 'Record e-ZLA',
      import: 'Import from PUE',
      employee: 'Employee',
      employeePlaceholder: 'Select an employee…',
      period: 'Period',
      days: 'Days',
      code: 'Code',
      codeNone: 'none',
      codeHint: {
        A: 'same illness within 60 days',
        B: 'pregnancy',
        C: 'alcohol-related',
        D: 'tuberculosis',
        E: 'infectious disease',
      },
      careForCol: 'Care of',
      careSelf: 'own illness',
      careChild: 'child',
      careFamily: 'family member',
      series: 'Series no.',
      source: 'Source',
      sourceManual: 'manual',
      sourcePue: 'PUE import',
      optional: 'optional',
      empty: 'No sick leaves recorded this year — enter an e-ZLA manually or import the PUE report.',
      dialogTitle: 'Record an e-ZLA certificate',
      dialogBody: 'ZUS does not notify employers — enter the certificate exactly as issued. Only the letter code is stored, never a diagnosis.',
      overrideLabel: 'Apply art. 166 KP — interrupt the overlapping vacation',
      save: 'Save',
      created: 'Sick leave recorded',
      splitsInfo: '{count} vacation request(s) adjusted (art. 166 KP)',
    },
    pue: {
      title: 'PUE / eZUS report import',
      step1: 'Upload the e-ZLA report exported from PUE ZUS (CSV or XLSX). Rows are matched by PESEL, then by name and date of birth.',
      selectFile: 'Choose a file',
      parsing: 'Analysing the file…',
      matched: 'Matched',
      unmatched: 'Unmatched',
      conflicts: 'Conflicts',
      matchCol: 'Match',
      matchPesel: 'matched by PESEL',
      matchNameDob: 'matched by name + DOB',
      matchNone: 'no matching employee',
      alreadyImported: 'already imported — skipped',
      conflictNote: 'overlaps approved vacation — importing applies art. 166 KP',
      rowError: 'row error: {error}',
      commit: 'Import {count} row(s)',
      back: 'Start over',
      cancel: 'Cancel',
      close: 'Close',
      done: 'Import finished: {created} imported, {skipped} skipped.',
    },
    stats: {
      absenceRate: 'Absence rate (this month)',
      ofWorkingTime: 'of scheduled working time',
      sickDaysYtd: 'Sick days ({year})',
      pendingRequests: 'Pending requests',
      outNext7: 'Away in the next 7 days',
      people: '{count} people',
      byType: 'Absence days by type ({year})',
      exportCsv: 'Export CSV',
      empty: 'No absence data yet.',
      yearLabel: 'Year',
      monthlyChartTitle: 'Absences month by month ({year})',
      topChartTitle: 'Top 10 employees by absence days ({year})',
      chartVacation: 'Annual leave',
      chartSick: 'Sick leave (L4)',
      chartOther: 'Other',
      tableTitle: 'Per-employee statistics ({year})',
      colEmployee: 'Employee',
      colDepartment: 'Department',
      colType: 'Contract type',
      colEntitlement: 'Entitled',
      colUsed: 'Used',
      colPending: 'Pending',
      colRemaining: 'Remaining',
      colOnDemand: 'On demand',
      colSick: 'Sick (L4)',
      colUnavailability: 'Unavailability',
      colRemote: 'Remote',
      colRate: 'Absence %',
    },
    common: {
      loadFailed: 'Failed to load HR data',
    },
    documents: {
      tab: 'Documents',
      title: 'Documents',
      pendingTitle: 'Pending acceptance',
      doneTitle: 'Accepted',
      libraryTitle: 'Document library',
      emptyPending: 'No documents waiting for acceptance.',
      emptyDone: 'No accepted documents yet.',
      emptyLibrary: 'The document library is empty.',
      deadline: 'Deadline: {date}',
      publishedAt: 'Published {date}',
      accept: 'Accept',
      download: 'Download PDF',
      signInstruction:
        'Download the PDF, sign it (by hand, or electronically at gov.pl) and send it back to periscope@fssrke3.com, or upload it below.',
      returnedNote: 'Submitted — awaiting HR confirmation',
      methods: {
        electronic: 'Electronic',
        physical: 'Signed scan',
      },
      acceptTitle: 'Confirm acceptance',
      acceptHint: 'Enter the last 4 digits of your PESEL to accept the document "{title}".',
      acceptInput: 'Last 4 digits of PESEL',
      acceptSubmit: 'Accept',
      acceptNote: 'The acceptance will be recorded with the date, document version and IP address.',
      acceptSuccess: 'Document accepted',
      acceptLocked: 'Acceptance is locked after too many failed attempts — ask HR to unlock it.',
      loadError: 'Failed to load documents',
      downloadError: 'Failed to download the document',
      acceptError: 'Failed to accept the document',
      uploadSigned: 'Upload signed scan',
      uploadFile: 'Scan file',
      uploadSubmit: 'Upload',
      uploadSuccess: 'Signed document submitted',
      uploadError: 'Failed to upload the document',
    },
  },

  registry: {
    lookup: 'Registry',
    fetch: 'Fetch from registry',
    lookupSuccess: 'Company data loaded from the government registry',
    lookupNotFound: 'No registry record found for this id',
    lookupFailed: 'Registry lookup failed — try again later',
    refresh: 'Refresh from registry',
    refreshSuccess: 'Contractor data refreshed from the registry',
    refreshFailed: 'Registry refresh failed',
    refreshNoId: 'Contractor needs a Polish NIP or an EU VAT number first',
    vatStatus: 'VAT status',
    vatStatusActive: 'Active VAT payer',
    vatStatusExempt: 'VAT exempt',
    vatStatusNotRegistered: 'Not registered for VAT',
    viesValid: 'VIES: valid VAT number',
    viesInvalid: 'VIES: invalid VAT number',
    verifiedAt: 'Verified {date}',
    neverVerified: 'Never verified in the registry',
    accountsTitle: 'White List bank accounts',
    accountsMatched: 'Stored accounts confirmed on the White List',
    accountsMissingLocally: 'On the White List but not stored here',
    accountsNotOnWhitelist: 'Stored accounts NOT on the White List',
    accountsAllMatched: 'All stored accounts are on the White List',
    whitelistWarning: 'This account is NOT on the VAT White List!',
    whitelistWarningDetail: 'Verify the vendor bank account before paying — payments over 15 000 PLN to unlisted accounts carry tax liability.',
    whitelistUnverified: 'White List: not verified',
    whitelistOk: 'Account confirmed on the VAT White List',
  },

  // Digital-signature verification of returned scans (PAdES)
  signatures: {
    kind: {
      qualified: 'Qualified signature',
      trusted_profile: 'Trusted profile (podpis.gov.pl)',
      other: 'Digital signature',
    },
    integrity: {
      valid: 'Integrity confirmed',
      invalid: 'File changed after signing',
      unverifiable: 'Could not be verified',
    },
    partialCoverage: 'partial coverage',
    restCoveredByLater: 'the rest is covered by a later signature',
    seal: 'Seal: {seal}',
    noSignature: 'no digital signature (paper scan)',
    unknownSigner: 'unknown signer',
    issuer: 'Issuer: {issuer}',
    signedAt: 'Signed: {date}',
    verifying: 'Verifying signature…',
    retry: 'Retry verification',
    verify: 'Verify signature',
  },
};

export type Translations = typeof en;
